import { LifeContext, JournalEntry, Decision, Pattern, ChatOpeningContext, DimensionImpacts, Language, AIPersonalityKey } from '../types';
import { getGraphInsights } from './graphService';
import { getPrompts } from '../constants/aiPrompts';
import { buildEnhancedContext, buildEnhancedContextWithSemantics, getTopScoredEntries, getPeopleContext, getPlacesContext, getTemporalPatterns, getDecisionLearning, EnhancedContext } from './contextIntelligence';
import { calculateAITier, getAITierDescription } from './gamificationService';
import { getAIBehaviorForTier, ANTI_GENERIC_RULES, ANTI_HALLUCINATION_RULES, getFriendModePrompts } from './aiTierService';
// NEW: Use unified personality system from shared module
import {
  getPersonality,
  buildUnifiedPersonalityPrompt,
  buildLightPersonalityPrompt,
  DEFAULT_PERSONALITY_KEY,
  AIPersonalityProfile,
  PromptContext
} from '../shared/aiPersonalities';

// ============================================
// AI PERSONALITY SYSTEM - Build prompt section
// ============================================
// NOW USES UNIFIED SYSTEM FROM shared/aiPersonalities.ts

/**
 * Build personality-aware prompt section for AI
 * This is a wrapper that delegates to the unified personality system.
 * Maintains backward compatibility while using the new psychology-based approach.
 */
export const buildPersonalityPromptSection = (
  personalityKey: AIPersonalityKey | undefined,
  language: Language,
  context: PromptContext = 'chat'
): string => {
  return buildUnifiedPersonalityPrompt(
    personalityKey || DEFAULT_PERSONALITY_KEY,
    language,
    context
  );
};

// ============================================
// USER FEEDBACK CONTEXT - For AI improvement
// ============================================

export interface UserFeedbackContext {
  rejectedInsights: string[];      // Insights user marked as not helpful
  rejectedBlockers: string[];      // Blockers user wants to exclude
  rejectedPatterns: string[];      // Patterns user wants to exclude
  helpfulPatterns: string[];       // Types of insights user found helpful
  feedbackStats: {
    totalHelpful: number;
    totalNotHelpful: number;
    preferredInsightTypes: string[];
  };
}

/**
 * Build feedback prompt section for AI to learn from user preferences
 */
export const buildFeedbackPromptSection = (
  feedback: UserFeedbackContext,
  language: string
): string => {
  const hasRejected = feedback && (
    feedback.rejectedInsights.length > 0 ||
    feedback.rejectedBlockers.length > 0 ||
    (feedback.rejectedPatterns?.length || 0) > 0
  );

  if (!hasRejected) {
    return '';
  }

  const labels: Record<string, { title: string; avoid: string; avoidPatterns: string; prefer: string }> = {
    Italian: {
      title: '🎯 PREFERENZE UTENTE (basate su feedback)',
      avoid: 'EVITA questi argomenti/approcci (utente li ha rifiutati):',
      avoidPatterns: 'EVITA di menzionare questi pattern (utente li ha rimossi):',
      prefer: 'Utente preferisce insight di tipo:'
    },
    English: {
      title: '🎯 USER PREFERENCES (based on feedback)',
      avoid: 'AVOID these topics/approaches (user rejected them):',
      avoidPatterns: 'AVOID mentioning these patterns (user removed them):',
      prefer: 'User prefers insight types:'
    },
    Spanish: {
      title: '🎯 PREFERENCIAS DEL USUARIO (basadas en feedback)',
      avoid: 'EVITA estos temas/enfoques (el usuario los rechazó):',
      avoidPatterns: 'EVITA mencionar estos patrones (el usuario los eliminó):',
      prefer: 'El usuario prefiere insights de tipo:'
    },
    German: {
      title: '🎯 BENUTZEREINSTELLUNGEN (basierend auf Feedback)',
      avoid: 'VERMEIDE diese Themen/Ansätze (Benutzer hat sie abgelehnt):',
      avoidPatterns: 'VERMEIDE diese Muster zu erwähnen (Benutzer hat sie entfernt):',
      prefer: 'Benutzer bevorzugt Insight-Typen:'
    },
    French: {
      title: '🎯 PRÉFÉRENCES UTILISATEUR (basées sur le feedback)',
      avoid: 'ÉVITEZ ces sujets/approches (l\'utilisateur les a rejetés):',
      avoidPatterns: 'ÉVITEZ de mentionner ces patterns (l\'utilisateur les a supprimés):',
      prefer: 'L\'utilisateur préfère les insights de type:'
    }
  };

  const l = labels[language] || labels.English;
  let section = `\n\n${l.title}\n`;

  // Add rejected insights and blockers as things to avoid
  const allRejected = [...feedback.rejectedInsights, ...feedback.rejectedBlockers];
  if (allRejected.length > 0) {
    section += `${l.avoid}\n`;
    allRejected.slice(0, 5).forEach(item => {
      section += `  ❌ "${item.substring(0, 80)}${item.length > 80 ? '...' : ''}"\n`;
    });
  }

  // Add rejected patterns
  if (feedback.rejectedPatterns?.length > 0) {
    section += `${l.avoidPatterns}\n`;
    feedback.rejectedPatterns.slice(0, 5).forEach(item => {
      section += `  ❌ "${item.substring(0, 80)}${item.length > 80 ? '...' : ''}"\n`;
    });
  }

  // Add preferred insight types if available
  if (feedback.feedbackStats.preferredInsightTypes.length > 0) {
    section += `${l.prefer} ${feedback.feedbackStats.preferredInsightTypes.join(', ')}\n`;
  }

  // Add ratio insight
  if (feedback.feedbackStats.totalHelpful + feedback.feedbackStats.totalNotHelpful > 5) {
    const ratio = Math.round(
      (feedback.feedbackStats.totalHelpful /
       (feedback.feedbackStats.totalHelpful + feedback.feedbackStats.totalNotHelpful)) * 100
    );
    if (ratio < 50) {
      section += `⚠️ Low satisfaction rate (${ratio}%) - try different approaches!\n`;
    }
  }

  return section;
};

// ============================================
// CHAT SESSION CONTEXT - Cached at chat start
// ============================================

export interface ChatSessionContext {
  enhancedContext: EnhancedContext;
  aiTier: string;
  tierDescription: { title: string; description: string };
  friendModePrompts: ReturnType<typeof getFriendModePrompts>;
  contextSummary: string;
  loadedAt: number;
}

/**
 * Initialize chat session context - call ONCE when chat opens.
 * This loads all the heavy context data upfront so subsequent messages are fast.
 */
export const initializeChatContext = async (
  context: LifeContext,
  entries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[]
): Promise<ChatSessionContext> => {
  const safeEntries = Array.isArray(entries) ? entries : [];

  // Build enhanced context with semantic search (the expensive operation)
  const enhancedContext = await buildEnhancedContextWithSemantics(
    '', // Empty query for initial load - will use general context
    context,
    safeEntries,
    decisions,
    patterns,
    [] // No conversation history yet
  );

  // Calculate AI tier
  const aiTier = calculateAITier(safeEntries.length, patterns.length);
  const tierDescription = getAITierDescription(aiTier);

  // Get friend mode prompts for user's language
  const friendModePrompts = getFriendModePrompts(context.language);

  // Build context summary
  const contextSummary = getContextSummary(context, safeEntries, patterns, decisions);

  return {
    enhancedContext,
    aiTier,
    tierDescription,
    friendModePrompts,
    contextSummary,
    loadedAt: Date.now()
  };
};

type ChatMessageContent = string | Array<{ type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string; detail?: string } }>;
type ChatMessage = { role: 'system' | 'user' | 'assistant'; content: ChatMessageContent };

// ============================================
// INSIGHT HISTORY TRACKING (Anti-Repetition)
// ============================================

interface InsightHistoryEntry {
  text: string;
  timestamp: number;
  type: 'pulse' | 'question' | 'pattern';
}

// In-memory cache for recent insights (per session)
// In production, this should be persisted to localStorage or DB
const insightHistory: InsightHistoryEntry[] = [];
const MAX_HISTORY_SIZE = 20;
const HISTORY_EXPIRY_HOURS = 24;

function addToInsightHistory(text: string, type: 'pulse' | 'question' | 'pattern') {
  // Clean old entries
  const now = Date.now();
  const expiryMs = HISTORY_EXPIRY_HOURS * 60 * 60 * 1000;
  while (insightHistory.length > 0 && now - insightHistory[0].timestamp > expiryMs) {
    insightHistory.shift();
  }

  // Add new entry
  insightHistory.push({ text, timestamp: now, type });

  // Trim to max size
  while (insightHistory.length > MAX_HISTORY_SIZE) {
    insightHistory.shift();
  }
}

function getRecentInsights(type?: 'pulse' | 'question' | 'pattern'): string[] {
  const filtered = type
    ? insightHistory.filter(h => h.type === type)
    : insightHistory;
  return filtered.map(h => h.text);
}

// Export for external use
export function clearInsightHistory() {
  insightHistory.length = 0;
}

async function aiChatCompletion(
  messages: ChatMessage[], 
  options: { temperature?: number; maxTokens?: number; jsonMode?: boolean; hasImage?: boolean } = {}
): Promise<string> {
  const response = await fetch('/api/ai/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ 
      messages, 
      temperature: options.temperature, 
      maxTokens: options.maxTokens,
      jsonMode: options.jsonMode,
      hasImage: options.hasImage
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'AI request failed');
  }
  const data = await response.json();
  return data.content || '';
}

const getContextSummary = (context: LifeContext, entries: JournalEntry[] = [], patterns: Pattern[] = [], decisions: Decision[] = []) => {
  const prompts = getPrompts(context.language);
  const cl = prompts.contextLabels;
  const values = context.values || [];
  const goals = context.goals || [];
  const safeEntries = Array.isArray(entries) ? entries : [];

  // Enhanced context aggregation with patterns and trends
  const recent = safeEntries.slice(0, 15).map(e =>
    `- ${new Date(e.timestamp).toLocaleDateString()}: ${e.content} (${e.category}, mood: ${e.emotion}, alignment: ${e.goalAlignment}%)`
  ).join("\n");

  // Calculate emotional patterns
  const emotionCounts = safeEntries.slice(0, 30).reduce((acc, e) => {
    acc[e.emotion] = (acc[e.emotion] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const dominantEmotions = Object.entries(emotionCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3)
    .map(([emoji]) => emoji)
    .join(' ');

  // Calculate category distribution
  const categoryDistribution = safeEntries.slice(0, 30).reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const focusAreas = Object.entries(categoryDistribution)
    .sort(([,a], [,b]) => b - a)
    .map(([cat, count]) => `${cat} (${count})`)
    .join(', ');

  // Average alignment over time
  const avgAlignment = safeEntries.length > 0
    ? Math.round(safeEntries.reduce((sum, e) => sum + (e.goalAlignment || 50), 0) / safeEntries.length)
    : 50;

  // Semantic graph insights
  const graphSummary = patterns.length > 0 || entries.length > 0
    ? `\n\n${getGraphInsights(entries, decisions, patterns)}`
    : '';

  // Pattern insights
  const patternInsights = patterns.length > 0
    ? `\n\n${cl.patternsIdentifiedBehavior}:
${patterns.slice(0, 5).map(p =>
  `• ${p.isPositive ? '✅' : '⚠️'} ${p.title} (${p.frequency}x) - ${p.insight}`
).join('\n')}`
    : '';

  // Semantic connections
  const recentConnections = safeEntries.slice(0, 5)
    .filter(e => e.relatedTo && e.relatedTo.length > 0)
    .map(e => {
      const mainLink = e.relatedTo![0];
      return `• "${e.content}" ${mainLink.linkType} ${cl.relatesToPastMoments}`;
    })
    .slice(0, 3)
    .join('\n');

  const connectionsInfo = recentConnections
    ? `\n\n${cl.semanticConnections}:
${recentConnections}`
    : '';

  // AI Profile context - deep knowledge about the user (multilingual headers)
  const aiProfile = context.aiProfile as any;
  let profileContext = '';
  
  const profileLabels = {
    Italian: {
      whoUserIs: 'CHI È L\'UTENTE',
      coreValues: 'VALORI FONDAMENTALI',
      lifeGoals: 'OBIETTIVI DI VITA',
      keyPeople: 'PERSONE IMPORTANTI',
      significantPlaces: 'LUOGHI SIGNIFICATIVI',
      personalityTraits: 'TRATTI DELLA PERSONALITÀ',
      deepKnowledge: 'PROFILO AI - CONOSCENZA PROFONDA DI'
    },
    Spanish: {
      whoUserIs: 'QUIÉN ES EL USUARIO',
      coreValues: 'VALORES FUNDAMENTALES',
      lifeGoals: 'OBJETIVOS DE VIDA',
      keyPeople: 'PERSONAS IMPORTANTES',
      significantPlaces: 'LUGARES SIGNIFICATIVOS',
      personalityTraits: 'RASGOS DE PERSONALIDAD',
      deepKnowledge: 'PERFIL AI - CONOCIMIENTO PROFUNDO DE'
    },
    German: {
      whoUserIs: 'WER DER BENUTZER IST',
      coreValues: 'KERNWERTE',
      lifeGoals: 'LEBENSZIELE',
      keyPeople: 'WICHTIGE MENSCHEN',
      significantPlaces: 'BEDEUTENDE ORTE',
      personalityTraits: 'PERSÖNLICHKEITSMERKMALE',
      deepKnowledge: 'KI-PROFIL - TIEFES WISSEN ÜBER'
    },
    French: {
      whoUserIs: 'QUI EST L\'UTILISATEUR',
      coreValues: 'VALEURS FONDAMENTALES',
      lifeGoals: 'OBJECTIFS DE VIE',
      keyPeople: 'PERSONNES IMPORTANTES',
      significantPlaces: 'LIEUX SIGNIFICATIFS',
      personalityTraits: 'TRAITS DE PERSONNALITÉ',
      deepKnowledge: 'PROFIL IA - CONNAISSANCE APPROFONDIE DE'
    },
    English: {
      whoUserIs: 'WHO THE USER IS',
      coreValues: 'CORE VALUES',
      lifeGoals: 'LIFE GOALS',
      keyPeople: 'KEY PEOPLE IN USER\'S LIFE',
      significantPlaces: 'SIGNIFICANT PLACES',
      personalityTraits: 'PERSONALITY TRAITS',
      deepKnowledge: 'AI PROFILE - DEEP KNOWLEDGE ABOUT'
    }
  };
  
  const pl = profileLabels[context.language as keyof typeof profileLabels] || profileLabels.English;
  
  if (aiProfile) {
    const profileParts: string[] = [];
    
    if (aiProfile.summary) {
      profileParts.push(`${pl.whoUserIs}: ${aiProfile.summary}`);
    }
    
    if (aiProfile.coreValues && aiProfile.coreValues.length > 0) {
      profileParts.push(`${pl.coreValues}:
${aiProfile.coreValues.slice(0, 5).map((v: any) => `• ${v.name} (${v.strength}% - ${v.trend || 'stable'})`).join('\n')}`);
    }
    
    if (aiProfile.lifeGoals && aiProfile.lifeGoals.length > 0) {
      profileParts.push(`${pl.lifeGoals}:
${aiProfile.lifeGoals.slice(0, 5).map((g: any) => `• ${g.goal} (${g.progress}%)${g.why ? ` - ${g.why}` : ''}`).join('\n')}`);
    }
    
    if (aiProfile.keyPeople && aiProfile.keyPeople.length > 0) {
      profileParts.push(`${pl.keyPeople}:
${aiProfile.keyPeople.slice(0, 5).map((p: any) => `• ${p.name} (${p.relationship})`).join('\n')}`);
    }
    
    if (aiProfile.keyPlaces && aiProfile.keyPlaces.length > 0) {
      profileParts.push(`${pl.significantPlaces}:
${aiProfile.keyPlaces.slice(0, 5).map((p: any) => `• ${p.name}: ${p.significance}`).join('\n')}`);
    }
    
    if (aiProfile.personalityTraits && aiProfile.personalityTraits.length > 0) {
      profileParts.push(`${pl.personalityTraits}:
${aiProfile.personalityTraits.slice(0, 5).map((t: any) => `• ${t.trait} (${t.strength}%)`).join('\n')}`);
    }
    
    if (aiProfile.blockers && aiProfile.blockers.length > 0) {
      const activeBlockers = aiProfile.blockers.filter((b: any) => b.status === 'active');
      if (activeBlockers.length > 0) {
        const challengesLabel = context.language === 'Italian' ? 'SFIDE ATTUALI' :
                               context.language === 'Spanish' ? 'DESAFÍOS ACTUALES' :
                               context.language === 'German' ? 'AKTUELLE HERAUSFORDERUNGEN' :
                               context.language === 'French' ? 'DÉFIS ACTUELS' : 'CURRENT CHALLENGES';
        profileParts.push(`${challengesLabel}:
${activeBlockers.slice(0, 5).map((b: any) => `• ${b.blocker} (${b.category}, severity: ${b.severity}/10)${b.context ? ` - ${b.context}` : ''}`).join('\n')}`);
      }
    }
    
    if (profileParts.length > 0) {
      profileContext = `\n\n=== ${pl.deepKnowledge} ${context.userName?.toUpperCase() || 'USER'} ===
${profileParts.join('\n\n')}`;
    }
  }

  return `${cl.userIdentity}: ${context.userName || cl.user}
${profileContext}

${cl.coreValuesSystem}:
${values.map(v => `• ${v}`).join('\n') || `• ${cl.inDefinition}`}

${cl.goalsAspirations}:
${goals.map(g => `• ${g}`).join('\n') || `• ${cl.inDefinition}`}

${cl.currentObstacles}:
${context.blockers?.map(b => `• ${b}`).join('\n') || `• ${cl.noBlockersIdentified}`}

${cl.behaviorAnalysis}:
• ${cl.avgGoalAlignment}: ${avgAlignment}%
• ${cl.dominantEmotionalPatterns}: ${dominantEmotions || cl.toCollect}
• ${cl.focusAreas}: ${focusAreas || cl.toIdentify}
${graphSummary}
${patternInsights}
${connectionsInfo}

${cl.recentHistory}:
${recent || cl.noActivityYet}

${cl.criticalNote}`;
};

export const analyzeMoment = async (
  text: string,
  context: LifeContext,
  imageBase64?: string,
  previousEntries: JournalEntry[] = [],
  patterns: Pattern[] = [],
  feedback?: UserFeedbackContext
): Promise<{
  title: string;
  emotion: string;
  aiInsight: string;
  alignment: number;
  goalAlignment: number;
  category: string;
  tags: string[];
  dimensionImpacts: DimensionImpacts;
  people?: Array<{ name: string; relationship?: string; impactOnMood?: number }>;
  places?: Array<{ name: string; type?: string; emotionalAssociation?: 'positive' | 'negative' | 'neutral' }>;
}> => {
  const prompts = getPrompts(context.language);
  const am = prompts.analyzeMoment;
  const safeEntries = Array.isArray(previousEntries) ? previousEntries : [];
  
  // Use ENHANCED CONTEXT for deeply personalized analysis
  const enhancedCtx = buildEnhancedContext(text, context, safeEntries, [], patterns);
  
  // Build people recognition context
  const knownPeopleStr = enhancedCtx.keyPeople.length > 0
    ? `\n\nKNOWN PEOPLE IN USER'S LIFE (recognize and reference these):
${enhancedCtx.keyPeople.slice(0, 8).map(p => `• ${p.name} (${p.relationship}) - ${p.emotionalTone} association, mentioned ${p.frequency}x`).join('\n')}`
    : '';
  
  // Build places recognition context
  const knownPlacesStr = enhancedCtx.keyPlaces.length > 0
    ? `\n\nKNOWN SIGNIFICANT PLACES (recognize and reference these):
${enhancedCtx.keyPlaces.slice(0, 5).map(p => `• ${p.name} (${p.type}) - ${p.emotionalAssociation} association`).join('\n')}`
    : '';
  
  // Build temporal insight
  const temporalStr = enhancedCtx.bestTimeOfDay !== 'not enough data'
    ? `\n\nTEMPORAL PATTERNS: User's best time is ${enhancedCtx.bestTimeOfDay}. Consider if this moment aligns with their natural rhythms.`
    : '';
  
  // Get recent relevant entries for connection
  const recentRelevantStr = enhancedCtx.topEntries.length > 0
    ? `\n\nRECENT RELATED MOMENTS (connect this new moment to user's history):
${enhancedCtx.topEntries.slice(0, 5).map(se => `• ${new Date(se.entry.timestamp).toLocaleDateString()}: "${se.entry.content.substring(0, 60)}..." (${se.entry.category}, ${se.entry.emotion})`).join('\n')}`
    : '';
  
  const feedbackSection = feedback ? buildFeedbackPromptSection(feedback, context.language) : '';

  // Add personality section for moment analysis
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);

  const systemPrompt = `${am.systemRole}
${personalitySection}
${am.contextIntro}
${getContextSummary(context, safeEntries, patterns)}
${knownPeopleStr}${knownPlacesStr}${temporalStr}${recentRelevantStr}

${feedbackSection}

${am.responseFormat}

ENHANCED RESPONSE FORMAT - Add these fields:
  "people": [{"name": "person name", "relationship": "amico/collega/partner/famiglia/etc", "impactOnMood": -100 to +100}],
  "places": [{"name": "place name", "type": "casa/ufficio/palestra/etc", "emotionalAssociation": "positive/negative/neutral"}]

CRITICAL PERSONALIZATION RULES:
- If user mentions a KNOWN person from their history, reference previous interactions in aiInsight
- If user mentions a KNOWN place, connect it to their emotional patterns there
- Make the aiInsight feel like it comes from someone who KNOWS the user deeply
- Reference patterns, goals, and values naturally in the insight
- The user should think "wow, this app really understands me"

${am.categoryRules}

${am.dimensionRules}`;

  try {
    const err = prompts.errorMessages;
    const userMessage: ChatMessage = imageBase64
      ? {
          role: 'user',
          content: [
            { type: 'text', text: text || err.analyzeThisMoment },
            { type: 'image_url', image_url: { url: imageBase64, detail: 'high' } }
          ]
        }
      : { role: 'user', content: text || err.analyzeThisMoment };

    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      userMessage
    ];

    const content = await aiChatCompletion(messages, { 
      jsonMode: true, 
      maxTokens: 2048,
      hasImage: !!imageBase64 
    });

    const parsed = JSON.parse(content || '{}');
    return {
      ...parsed,
      dimensionImpacts: parsed.dimensionImpacts || { salute: 0, focus: 0, connessione: 0, spirito: 0, finanza: 0 },
      people: parsed.people || [],
      places: parsed.places || []
    };
  } catch (error) {
    console.error("AI Analysis error:", error);
    const err = prompts.errorMessages;
    return {
      title: text || err.newMoment,
      emotion: '✨',
      aiInsight: err.momentRecorded,
      alignment: 50,
      goalAlignment: 50,
      category: 'Focus',
      tags: [],
      dimensionImpacts: { salute: 0, focus: 0, connessione: 0, spirito: 0, finanza: 0 },
      people: [],
      places: []
    };
  }
};

export const getLifeIntelligenceAdvice = async (
  query: string,
  context: LifeContext,
  entries: JournalEntry[],
  decisions: Decision[],
  imageBase64?: string,
  patterns: Pattern[] = []
): Promise<string> => {
  const prompts = getPrompts(context.language);
  const li = prompts.lifeIntelligence;
  const summary = getContextSummary(context, entries, patterns, decisions);
  const p = context.aiPersonality || { warmth: 80, directness: 50, depth: 50 };
  
  const recentDecisions = decisions.slice(0, 5).map(d =>
    `- "${d.title}" (${d.status === 'resolved' ? '✓' : '⏳'})`
  ).join('\n');

  // Add personality section
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);

  const systemPrompt = `${li.systemRole}
${personalitySection}
${li.toneIntro}
- ${p.warmth}% (${p.warmth > 70 ? li.warmthHigh : p.warmth > 40 ? li.warmthMid : li.warmthLow})
- ${p.directness}% (${p.directness > 70 ? li.directnessHigh : p.directness > 40 ? li.directnessMid : li.directnessLow})
- ${p.depth}% (${p.depth > 70 ? li.depthHigh : p.depth > 40 ? li.depthMid : li.depthLow})

${summary}

${li.recentDecisions}
${recentDecisions || li.noDecisions}

${li.instructions}`;

  try {
    const userMessage: ChatMessage = imageBase64
      ? {
          role: 'user',
          content: [
            { type: 'text', text: query },
            { type: 'image_url', image_url: { url: imageBase64, detail: 'high' } }
          ]
        }
      : { role: 'user', content: query };

    const result = await aiChatCompletion(
      [{ role: 'system', content: systemPrompt }, userMessage],
      { maxTokens: 4096, hasImage: !!imageBase64 }
    );

    const err = prompts.errorMessages;
    return result || err.couldNotProcess;
  } catch (error) {
    console.error("Life Intelligence error:", error);
    const err = prompts.errorMessages;
    return err.connectionUnstable;
  }
};

export const mapDecision = async (
  text: string,
  context: LifeContext,
  entries: JournalEntry[],
  patterns: Pattern[] = [],
  decisions: Decision[] = [],
  feedback?: UserFeedbackContext
): Promise<{
  analysisSummary: string;
  bestOption: string;
  reasonWhy: string;
  nextAction: string;
  options: Array<{
    id: string;
    title: string;
    pros: string[];
    cons: string[];
    alignmentScore: number;
  }>;
}> => {
  const prompts = getPrompts(context.language);
  const dm = prompts.decisionMapping;
  const summary = getContextSummary(context, entries, patterns, decisions);
  const feedbackSection = feedback ? buildFeedbackPromptSection(feedback, context.language) : '';

  // Add personality section
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);

  const systemPrompt = `${dm.systemRole}
${personalitySection}
${dm.contextIntro}
${summary}

${feedbackSection}

${dm.decisionIntro}

${dm.analysisRules}

${dm.responseFormat}`;

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: text }
      ],
      { jsonMode: true, maxTokens: 4096 }
    );
    return JSON.parse(content || '{}');
  } catch (error) {
    console.error("Decision mapping error:", error);
    const err = prompts.errorMessages;
    return {
      analysisSummary: err.cannotMapNow,
      options: [],
      bestOption: err.thinkCalmly,
      reasonWhy: err.technicalError,
      nextAction: err.retryInMinutes
    };
  }
};

// ============================================
// INTELLIGENT INSIGHT TYPE SELECTION
// Selects insight type based on emotional context, not random
// ============================================

type InsightType =
  | 'pattern_observation'
  | 'gentle_challenge'
  | 'hidden_truth'
  | 'celebration'
  | 'connection'
  | 'future_seed'
  | 'gentle_support'
  | 'progress_highlight'
  | 'milestone'
  | 'relationship_insight'
  | 'memory_callback';  // NEW: Reference old entry to show deep memory

// Enhanced insight result with optional memory link
export interface EnhancedInsight {
  text: string;                    // Main insight (max 20 words)
  memoryLink?: {                   // Optional link to old entry
    date: string;                  // Formatted date
    preview: string;               // Short preview of old entry
    entryId: string;               // For navigation
  };
  insightType: InsightType;        // Type that was used
  emotionalContext: string;        // Current emotional state
}

interface InsightTypeWeight {
  type: InsightType;
  weight: number;
}

// Weighted random selection
const weightedRandomSelect = (options: InsightTypeWeight[]): InsightType => {
  const totalWeight = options.reduce((sum, o) => sum + o.weight, 0);
  let random = Math.random() * totalWeight;

  for (const option of options) {
    random -= option.weight;
    if (random <= 0) return option.type;
  }
  return options[0].type;
};

// Check for milestones (streaks, achievements)
const detectMilestone = (entries: JournalEntry[], patterns: Pattern[], language: Language = 'Italian'): { hasMilestone: boolean; description?: string; daysActive?: number } => {
  if (entries.length < 3) return { hasMilestone: false };

  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;

  // Calculate total days since first entry (not streak, but total journey)
  const sortedByDate = [...entries].sort((a, b) => a.timestamp - b.timestamp);
  const firstEntryDate = new Date(sortedByDate[0].timestamp);
  const daysSinceStart = Math.floor((now - firstEntryDate.getTime()) / DAY_MS) + 1;

  // Check for consecutive days of entries (including yesterday if today has no entry yet)
  const allDays = new Set<string>();
  for (const entry of entries) {
    const date = new Date(entry.timestamp).toDateString();
    allDays.add(date);
  }

  // Count consecutive days - start from yesterday if today has no entry
  const todayStr = new Date(now).toDateString();
  const yesterdayStr = new Date(now - DAY_MS).toDateString();

  let streak = 0;
  const startFromYesterday = !allDays.has(todayStr) && allDays.has(yesterdayStr);
  const startOffset = startFromYesterday ? 1 : 0;

  for (let i = startOffset; i < 60; i++) {
    const checkDate = new Date(now - i * DAY_MS).toDateString();
    if (allDays.has(checkDate)) {
      streak++;
    } else {
      break;
    }
  }

  // Localized milestone messages
  const milestoneMessages: Record<Language, Record<string, string>> = {
    Italian: {
      week: `${streak} giorni consecutivi di journaling!`,
      twoWeeks: `${streak} giorni consecutivi!`,
      threeWeeks: `3 settimane consecutive!`,
      month: `Un mese intero di journaling!`,
      pattern: (title: string) => `Pattern forte: ${title}`
    },
    English: {
      week: `${streak} consecutive days of journaling!`,
      twoWeeks: `${streak} consecutive days!`,
      threeWeeks: `3 consecutive weeks!`,
      month: `A full month of journaling!`,
      pattern: (title: string) => `Strong pattern: ${title}`
    },
    Spanish: {
      week: `${streak} días consecutivos de journaling!`,
      twoWeeks: `${streak} días consecutivos!`,
      threeWeeks: `3 semanas consecutivas!`,
      month: `¡Un mes entero de journaling!`,
      pattern: (title: string) => `Patrón fuerte: ${title}`
    },
    German: {
      week: `${streak} aufeinanderfolgende Tage Journaling!`,
      twoWeeks: `${streak} aufeinanderfolgende Tage!`,
      threeWeeks: `3 aufeinanderfolgende Wochen!`,
      month: `Ein ganzer Monat Journaling!`,
      pattern: (title: string) => `Starkes Muster: ${title}`
    },
    French: {
      week: `${streak} jours consécutifs de journaling!`,
      twoWeeks: `${streak} jours consécutifs!`,
      threeWeeks: `3 semaines consécutives!`,
      month: `Un mois entier de journaling!`,
      pattern: (title: string) => `Schéma fort: ${title}`
    }
  };

  const msgs = milestoneMessages[language] || milestoneMessages.English;

  // Milestone thresholds - now with ranges, not exact matches
  if (streak >= 30) return { hasMilestone: true, description: msgs.month, daysActive: daysSinceStart };
  if (streak >= 21) return { hasMilestone: true, description: msgs.threeWeeks, daysActive: daysSinceStart };
  if (streak >= 14) return { hasMilestone: true, description: msgs.twoWeeks, daysActive: daysSinceStart };
  if (streak >= 7) return { hasMilestone: true, description: msgs.week, daysActive: daysSinceStart };

  // Check for positive pattern streaks
  const positivePatterns = patterns.filter(p => p.isPositive && p.frequency >= 5);
  if (positivePatterns.length > 0) {
    const strongPattern = positivePatterns.find(p => p.frequency >= 7);
    if (strongPattern) {
      const patternMsg = typeof msgs.pattern === 'function' ? msgs.pattern(strongPattern.title) : `Pattern: ${strongPattern.title}`;
      return { hasMilestone: true, description: patternMsg, daysActive: daysSinceStart };
    }
  }

  return { hasMilestone: false, daysActive: daysSinceStart };
};

// ============================================
// MEMORY SURPRISE SYSTEM
// Find old entries that create "wow, you remember that?" moments
// ============================================

interface MemorySurprise {
  found: boolean;
  oldEntry?: JournalEntry;
  contrast?: string;           // Description of then vs now
  relevanceReason?: string;    // Why this memory is relevant
  formattedDate?: string;      // Localized date
}

const findMemorySurprise = (
  entries: JournalEntry[],
  currentContext: {
    recentTopics: string[];    // Topics from recent entries
    currentMood: string;
    dominantCategory: string;
    keyPeopleNames: string[];
    goals: string[];
  },
  language: Language
): MemorySurprise => {
  if (entries.length < 20) return { found: false };

  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;

  // Find entries from 2-6 months ago
  const oldEntries = entries.filter(e => {
    const age = now - e.timestamp;
    return age > 60 * DAY_MS && age < 180 * DAY_MS;
  });

  if (oldEntries.length === 0) return { found: false };

  // Score old entries by relevance to current context
  const scoredEntries = oldEntries.map(entry => {
    let score = 0;
    const contentLower = entry.content.toLowerCase();

    // Check for same people mentioned
    for (const person of currentContext.keyPeopleNames) {
      if (contentLower.includes(person.toLowerCase())) {
        score += 30;
      }
    }

    // Check for same goals/topics
    for (const goal of currentContext.goals) {
      if (contentLower.includes(goal.toLowerCase().substring(0, 10))) {
        score += 20;
      }
    }

    // Check for same category
    if (entry.category === currentContext.dominantCategory) {
      score += 15;
    }

    // Check for topic overlap with recent entries
    for (const topic of currentContext.recentTopics) {
      if (contentLower.includes(topic.toLowerCase())) {
        score += 10;
      }
    }

    // Bonus for emotional entries (more memorable)
    const emotionalEmojis = ['😢', '😡', '🎉', '😍', '💪', '😰', '😤', '🥺', '💔'];
    if (emotionalEmojis.includes(entry.emotion)) {
      score += 10;
    }

    // Bonus for low alignment entries that contrast with current improvement
    if (entry.goalAlignment < 40) {
      score += 15; // Good for "look how far you've come" narrative
    }

    return { entry, score };
  });

  // Get best match
  const bestMatch = scoredEntries.sort((a, b) => b.score - a.score)[0];

  if (bestMatch.score < 25) return { found: false }; // Not relevant enough

  const entry = bestMatch.entry;
  const entryDate = new Date(entry.timestamp);

  // Format date based on language
  const dateFormatters: Record<Language, (d: Date) => string> = {
    Italian: (d) => d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
    English: (d) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    Spanish: (d) => d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }),
    German: (d) => d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
    French: (d) => d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  };

  const formattedDate = dateFormatters[language](entryDate);

  // Build contrast narrative
  const contrastPhrases: Record<Language, (old: JournalEntry) => string> = {
    Italian: (old) => `A ${formattedDate} scrivevi: "${old.content.substring(0, 60)}..."`,
    English: (old) => `In ${formattedDate} you wrote: "${old.content.substring(0, 60)}..."`,
    Spanish: (old) => `En ${formattedDate} escribiste: "${old.content.substring(0, 60)}..."`,
    German: (old) => `Im ${formattedDate} schriebst du: "${old.content.substring(0, 60)}..."`,
    French: (old) => `En ${formattedDate} tu écrivais: "${old.content.substring(0, 60)}..."`
  };

  return {
    found: true,
    oldEntry: entry,
    contrast: contrastPhrases[language](entry),
    formattedDate,
    relevanceReason: bestMatch.score >= 40 ? 'high_relevance' : 'moderate_relevance'
  };
};

// Extract recent topics from entries for memory matching
const extractRecentTopics = (entries: JournalEntry[]): string[] => {
  const topics: string[] = [];
  const recentEntries = entries.slice(0, 10);

  for (const entry of recentEntries) {
    // Extract significant words (4+ chars, not common words)
    const words = entry.content.split(/\s+/)
      .filter(w => w.length >= 4)
      .map(w => w.toLowerCase().replace(/[^a-zàèéìòù]/g, ''))
      .filter(w => !['questo', 'quella', 'molto', 'sono', 'stata', 'stato', 'oggi', 'ieri', 'sempre', 'this', 'that', 'have', 'been', 'with', 'from'].includes(w));

    topics.push(...words.slice(0, 5));
  }

  // Return unique topics
  return [...new Set(topics)].slice(0, 20);
};

// Intelligent insight type selection based on emotional context
const selectInsightType = (
  emotionalPulse: {
    recentShift: 'improving' | 'declining' | 'stable' | 'volatile';
    streakType: 'positive' | 'negative' | 'mixed' | null;
    streakDays: number;
    emotionalIntensity: 'low' | 'medium' | 'high' | 'extreme';
  },
  entries: JournalEntry[],
  patterns: Pattern[],
  hasLovedOnes: boolean,
  hasMemorySurprise: boolean = false  // NEW: Whether a relevant old memory was found
): InsightType => {

  const milestone = detectMilestone(entries, patterns);

  // PRIORITY 1: If there's a milestone, celebrate it (70% chance)
  if (milestone.hasMilestone && Math.random() < 0.7) {
    return 'milestone';
  }

  // PRIORITY 2: Memory callback when relevant old entry found (30% chance)
  // Creates "wow you remember that?" moments
  if (hasMemorySurprise && Math.random() < 0.3) {
    return 'memory_callback';
  }

  // PRIORITY 3: User is struggling - be supportive, not challenging
  if (emotionalPulse.recentShift === 'declining' || emotionalPulse.streakType === 'negative') {
    return weightedRandomSelect([
      { type: 'celebration', weight: 30 },        // Find something positive
      { type: 'gentle_support', weight: 25 },     // Be supportive
      { type: 'progress_highlight', weight: 20 }, // Show past progress to encourage
      { type: 'memory_callback', weight: 15 },    // "Remember when... you got through it"
      { type: 'future_seed', weight: 10 }         // Plant hope
    ]);
  }

  // PRIORITY 4: Volatile emotions - be gentle and connecting
  if (emotionalPulse.recentShift === 'volatile' || emotionalPulse.emotionalIntensity === 'extreme') {
    return weightedRandomSelect([
      { type: 'connection', weight: 30 },          // Help make sense of ups and downs
      { type: 'gentle_support', weight: 25 },
      { type: 'pattern_observation', weight: 20 }, // Gently show what's happening
      { type: 'memory_callback', weight: 15 },     // Ground them with past experience
      { type: 'future_seed', weight: 10 }
    ]);
  }

  // PRIORITY 5: User is doing well - can challenge more
  if (emotionalPulse.recentShift === 'improving' || emotionalPulse.streakType === 'positive') {
    // Memory callback is powerful here: "Look how far you've come!"
    if (hasMemorySurprise && Math.random() < 0.35) {
      return 'memory_callback';
    }

    if (hasLovedOnes && Math.random() < 0.25) {
      return 'relationship_insight';
    }

    return weightedRandomSelect([
      { type: 'gentle_challenge', weight: 25 },
      { type: 'celebration', weight: 20 },
      { type: 'pattern_observation', weight: 20 },
      { type: 'connection', weight: 15 },
      { type: 'progress_highlight', weight: 10 },
      { type: 'future_seed', weight: 10 }
    ]);
  }

  // DEFAULT: Stable/neutral - balanced mix
  if (hasLovedOnes && Math.random() < 0.2) {
    return 'relationship_insight';
  }

  if (hasMemorySurprise && Math.random() < 0.25) {
    return 'memory_callback';
  }

  return weightedRandomSelect([
    { type: 'pattern_observation', weight: 25 },
    { type: 'connection', weight: 20 },
    { type: 'gentle_challenge', weight: 15 },
    { type: 'celebration', weight: 15 },
    { type: 'future_seed', weight: 15 },
    { type: 'progress_highlight', weight: 10 }
  ]);
};

// ============================================
// DYNAMIC TONE GUIDELINES
// Adapts tone based on emotional state
// ============================================

const getDynamicToneGuidelines = (
  emotionalPulse: {
    recentShift: 'improving' | 'declining' | 'stable' | 'volatile';
    streakType: 'positive' | 'negative' | 'mixed' | null;
    emotionalIntensity: 'low' | 'medium' | 'high' | 'extreme';
  },
  language: Language
): string => {

  // User is struggling
  if (emotionalPulse.recentShift === 'declining' || emotionalPulse.streakType === 'negative') {
    const tones: Record<Language, string> = {
      Italian: `
🫂 TONO RICHIESTO: Caldo, supportivo, NON giudicante
- Riconosci che è un periodo difficile
- Trova UNA cosa positiva, anche piccola
- VIETATO: sfide, "dovresti", critiche velate
- Esempio buono: "Anche nei giorni pesanti, sei qui. Questo conta."
- Esempio buono: "La stanchezza che senti è reale. Ma anche la tua resilienza."`,
      English: `
🫂 REQUIRED TONE: Warm, supportive, NOT judgmental
- Acknowledge it's a tough time
- Find ONE positive thing, even small
- FORBIDDEN: challenges, "you should", veiled criticism
- Good example: "Even on heavy days, you're here. That matters."`,
      Spanish: `
🫂 TONO REQUERIDO: Cálido, de apoyo, NO crítico
- Reconoce que es un momento difícil
- Encuentra UNA cosa positiva, aunque sea pequeña
- PROHIBIDO: desafíos, "deberías", críticas veladas`,
      German: `
🫂 ERFORDERLICHER TON: Warm, unterstützend, NICHT wertend
- Erkenne an, dass es eine schwierige Zeit ist
- Finde EINE positive Sache, auch wenn klein
- VERBOTEN: Herausforderungen, "du solltest", versteckte Kritik`,
      French: `
🫂 TON REQUIS: Chaleureux, bienveillant, NON jugeant
- Reconnaître que c'est une période difficile
- Trouver UNE chose positive, même petite
- INTERDIT: défis, "tu devrais", critiques voilées`
    };
    return tones[language] || tones.English;
  }

  // User emotions are volatile
  if (emotionalPulse.recentShift === 'volatile' || emotionalPulse.emotionalIntensity === 'extreme') {
    const tones: Record<Language, string> = {
      Italian: `
🌊 TONO RICHIESTO: Calmo, centrato, stabilizzante
- Le emozioni sono altalenanti - sii un'ancora
- Aiuta a trovare un filo conduttore
- Normalizza senza minimizzare
- Esempio: "Alti e bassi intensi. In mezzo a tutto, cosa resta costante?"`,
      English: `
🌊 REQUIRED TONE: Calm, centered, stabilizing
- Emotions are volatile - be an anchor
- Help find a common thread
- Normalize without minimizing`,
      Spanish: `
🌊 TONO REQUERIDO: Calmo, centrado, estabilizador
- Las emociones son volátiles - sé un ancla
- Ayuda a encontrar un hilo conductor`,
      German: `
🌊 ERFORDERLICHER TON: Ruhig, zentriert, stabilisierend
- Emotionen schwanken - sei ein Anker
- Hilf, einen roten Faden zu finden`,
      French: `
🌊 TON REQUIS: Calme, centré, stabilisant
- Les émotions sont volatiles - sois une ancre
- Aide à trouver un fil conducteur`
    };
    return tones[language] || tones.English;
  }

  // User is doing well - can be more direct
  if (emotionalPulse.recentShift === 'improving' || emotionalPulse.streakType === 'positive') {
    const tones: Record<Language, string> = {
      Italian: `
💪 TONO RICHIESTO: Diretto, stimolante, celebrativo
- Riconosci i progressi
- Puoi permetterti sfide costruttive
- Spingi verso il prossimo livello
- Esempio: "Stai volando. E se alzassi l'asticella?"
- Esempio: "Questo momentum è raro. Cosa vuoi conquistare adesso?"`,
      English: `
💪 REQUIRED TONE: Direct, stimulating, celebratory
- Acknowledge progress
- Can include constructive challenges
- Push toward the next level
- Example: "You're flying. What if you raised the bar?"`,
      Spanish: `
💪 TONO REQUERIDO: Directo, estimulante, celebratorio
- Reconoce los progresos
- Puedes incluir desafíos constructivos`,
      German: `
💪 ERFORDERLICHER TON: Direkt, anregend, feierlich
- Erkenne Fortschritte an
- Kann konstruktive Herausforderungen enthalten`,
      French: `
💪 TON REQUIS: Direct, stimulant, célébratoire
- Reconnaître les progrès
- Peut inclure des défis constructifs`
    };
    return tones[language] || tones.English;
  }

  // Default: balanced tone
  const defaultTones: Record<Language, string> = {
    Italian: `
⚖️ TONO: Equilibrato, presente, autentico
- Né troppo entusiasta né troppo serio
- Come un amico che ti conosce bene
- Può osservare, celebrare, o sfidare gentilmente in base al contenuto`,
    English: `
⚖️ TONE: Balanced, present, authentic
- Neither too enthusiastic nor too serious
- Like a friend who knows you well`,
    Spanish: `⚖️ TONO: Equilibrado, presente, auténtico`,
    German: `⚖️ TON: Ausgewogen, präsent, authentisch`,
    French: `⚖️ TON: Équilibré, présent, authentique`
  };
  return defaultTones[language] || defaultTones.English;
};

export const getPulseInsight = async (
  context: LifeContext,
  entries: JournalEntry[],
  patterns: Pattern[] = [],
  decisions: Decision[] = [],
  feedback?: UserFeedbackContext
): Promise<EnhancedInsight> => {
  const prompts = getPrompts(context.language);
  const pi = prompts.pulseInsight;
  const safeEntries = Array.isArray(entries) ? entries : [];

  // Welcome messages for new users with no or very few entries
  if (safeEntries.length === 0) {
    const welcomeMessages: Record<string, string> = {
      Italian: `Ciao ${context.userName || ''}! Inizia a raccontarmi il tuo primo momento.`,
      English: `Hi ${context.userName || ''}! Start by sharing your first moment with me.`,
      Spanish: `Hola ${context.userName || ''}! Empieza contandome tu primer momento.`,
      German: `Hallo ${context.userName || ''}! Erzahl mir deinen ersten Moment.`,
      French: `Salut ${context.userName || ''}! Commence par me raconter ton premier moment.`
    };
    return {
      text: welcomeMessages[context.language] || welcomeMessages.English,
      insightType: 'future_seed' as InsightType,
      emotionalContext: 'welcome'
    };
  }

  if (safeEntries.length < 3) {
    const earlyMessages: Record<string, string> = {
      Italian: `${context.userName || 'Tu'}, ogni momento che condividi mi aiuta a conoscerti meglio.`,
      English: `${context.userName || 'You'}, every moment you share helps me know you better.`,
      Spanish: `${context.userName || 'Tu'}, cada momento que compartes me ayuda a conocerte mejor.`,
      German: `${context.userName || 'Du'}, jeder Moment, den du teilst, hilft mir dich besser kennenzulernen.`,
      French: `${context.userName || 'Toi'}, chaque moment que tu partages m'aide a mieux te connaitre.`
    };
    return {
      text: earlyMessages[context.language] || earlyMessages.English,
      insightType: 'gentle_support' as InsightType,
      emotionalContext: 'early_stage'
    };
  }

  // Use ENHANCED CONTEXT for deeply personalized pulse
  const enhancedCtx = buildEnhancedContext('daily insight', context, safeEntries, decisions, patterns);

  // Build rich context with contradictions, blockers, and deep patterns
  // IMPROVED: Include relationship context and sentiment for more accurate insights
  const lovedOnes = enhancedCtx.keyPeople.filter(p => p.isLovedOne);
  const lovedOnesStr = lovedOnes.length > 0
    ? `\n❤️ LOVED ONES (family/partner - NEVER interpret negatively without explicit conflict evidence): ${lovedOnes.map(p => `${p.name} (${p.relationship}, sentiment: ${p.sentimentContext})`).join(', ')}`
    : '';
  const otherPeople = enhancedCtx.keyPeople.filter(p => !p.isLovedOne);
  const peopleStr = otherPeople.length > 0
    ? `\nOther key people: ${otherPeople.slice(0, 3).map(p => `${p.name} (${p.emotionalTone})`).join(', ')}`
    : '';
  const placesStr = enhancedCtx.keyPlaces.length > 0
    ? `\nSignificant places: ${enhancedCtx.keyPlaces.slice(0, 2).map(p => `${p.name} (${p.contextDescription})`).join(', ')}`
    : '';
  const temporalStr = enhancedCtx.bestTimeOfDay !== 'not enough data'
    ? `\nBest time: ${enhancedCtx.bestTimeOfDay}, Best day: ${enhancedCtx.bestDayOfWeek}`
    : '';
  const trendStr = `\nCurrent trend: ${enhancedCtx.alignmentTrend}, Mood: ${enhancedCtx.currentMood}, Focus area: ${enhancedCtx.dominantCategory}`;

  // Extract blockers and patterns for deeper insight
  const blockers = context.blockers || [];
  const blockersStr = blockers.length > 0
    ? `\nActive blockers: ${blockers.slice(0, 2).join(', ')}`
    : '';

  // Find contradictions between values and actions
  const values = context.values || [];
  const goals = context.goals || [];
  const recentCategories = safeEntries.slice(0, 10).map(e => e.category);
  const categoryCount = recentCategories.reduce((acc, c) => { acc[c] = (acc[c] || 0) + 1; return acc; }, {} as Record<string, number>);
  const dominantCategory = Object.entries(categoryCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '';

  // Detect potential contradictions
  const contradictionsHints: string[] = [];
  if (values.includes('Famiglia') || values.includes('Family')) {
    const workCount = categoryCount['Career'] || categoryCount['Lavoro'] || 0;
    const connectionCount = categoryCount['Connection'] || categoryCount['Connessione'] || 0;
    if (workCount > connectionCount * 2) {
      contradictionsHints.push('Says family is important but spends more time on work');
    }
  }
  if (values.includes('Salute') || values.includes('Health')) {
    const healthCount = categoryCount['Health'] || categoryCount['Salute'] || 0;
    if (healthCount === 0 && safeEntries.length > 5) {
      contradictionsHints.push('Values health but rarely tracks health moments');
    }
  }

  // Get recent insights to avoid repetition
  const recentPulseInsights = getRecentInsights('pulse');
  const avoidList = recentPulseInsights.length > 0
    ? `\n\n⛔ DO NOT REPEAT THESE (already shown recently):\n${recentPulseInsights.slice(-5).map(i => `- "${i}"`).join('\n')}`
    : '';

  // INTELLIGENT insight type selection based on emotional context
  const emotionalPulse = enhancedCtx.emotionalPulse || {
    recentShift: 'stable' as const,
    streakType: null,
    streakDays: 0,
    emotionalIntensity: 'medium' as const
  };
  const hasLovedOnes = lovedOnes.length > 0;

  // MEMORY SURPRISE: Find relevant old entry for "wow you remember" moments
  const recentTopics = extractRecentTopics(safeEntries);
  const memorySurprise = findMemorySurprise(
    safeEntries,
    {
      recentTopics,
      currentMood: enhancedCtx.currentMood,
      dominantCategory: enhancedCtx.dominantCategory,
      keyPeopleNames: enhancedCtx.keyPeople.map(p => p.name),
      goals: context.goals || []
    },
    context.language
  );

  const selectedType = selectInsightType(emotionalPulse, safeEntries, patterns, hasLovedOnes, memorySurprise.found);

  // Get dynamic tone based on emotional state
  const dynamicTone = getDynamicToneGuidelines(emotionalPulse, context.language);

  // Milestone info for context
  const milestoneInfo = detectMilestone(safeEntries, patterns, context.language);

  // Memory context for memory_callback type
  const memoryContext = memorySurprise.found
    ? `\n🧠 MEMORY FOUND: ${memorySurprise.contrast}`
    : '';

  const personalizedContext = `${enhancedCtx.summary}${lovedOnesStr}${peopleStr}${placesStr}${temporalStr}${trendStr}${blockersStr}`;

  // Deep insight prompt - brutally honest like a psychologist (multilingual)
  const getDeepInsightPrompt = (lang: Language): string => {
    const userName = context.userName || (lang === 'Italian' ? 'questa persona' : lang === 'Spanish' ? 'esta persona' : lang === 'German' ? 'diese Person' : lang === 'French' ? 'cette personne' : 'this person');
    const valuesStr = values.join(', ') || (lang === 'Italian' ? 'non definiti' : lang === 'Spanish' ? 'no definidos' : lang === 'German' ? 'nicht definiert' : lang === 'French' ? 'non définis' : 'not defined');
    const goalsStr = goals.join(', ') || (lang === 'Italian' ? 'non definiti' : lang === 'Spanish' ? 'no definidos' : lang === 'German' ? 'nicht definiert' : lang === 'French' ? 'non définis' : 'not defined');
    const patternsStr = patterns.slice(0, 3).map(p => `${p.title} (${p.isPositive ? '✅' : '⚠️'})`).join(', ') || (lang === 'Italian' ? 'in analisi' : lang === 'Spanish' ? 'analizando' : lang === 'German' ? 'analysieren' : lang === 'French' ? 'en analyse' : 'analyzing');

    // Milestone context for milestone type
    const milestoneContext = milestoneInfo.hasMilestone
      ? `\n🏆 MILESTONE DETECTED: ${milestoneInfo.description}`
      : '';

    const typeHints: Record<Language, Record<string, string>> = {
      Italian: {
        gentle_challenge: '→ Sfida gentilmente, poi suggerisci un\'azione. Es: "Dici X ma fai Y. E se provassi...?"',
        hidden_truth: '→ Verità scomoda ma con amore. Termina con domanda o invito.',
        pattern_observation: '→ Evidenzia pattern + suggerisci azione. "Noto che X. Hai provato Y?"',
        celebration: '→ Celebra + rinforza. "Hai fatto X! Come puoi mantenerlo?"',
        connection: '→ Collega eventi + domanda. "Quando X, poi Y. Coincidenza?"',
        future_seed: '→ Pianta seme + invito. "E se domani provassi X?"',
        gentle_support: '→ Supporto caldo. "Anche nei giorni difficili, X. Questo conta."',
        progress_highlight: '→ Mostra progresso. "A [mese] eri X. Ora Y. Guarda quanta strada."',
        milestone: `→ CELEBRA! ${milestoneInfo.description || ''} "X giorni! Non fortuna, determinazione."`,
        relationship_insight: '→ Insight relazione + domanda. "Con X i momenti migliori sono Y. Quando è stata l\'ultima volta?"',
        memory_callback: `→ USA QUESTA MEMORIA: ${memorySurprise.contrast || ''} Crea contrasto presente/passato. "Ricordo quando scrivevi X. Ora Y."`
      },
      English: {
        gentle_challenge: '→ Challenge + suggest action. "You say X but do Y. What if you tried...?"',
        hidden_truth: '→ Uncomfortable truth with love. End with question or invitation.',
        pattern_observation: '→ Show pattern + suggest action. "I notice X. Have you tried Y?"',
        celebration: '→ Celebrate + reinforce. "You did X! How can you keep it up?"',
        connection: '→ Connect events + question. "When X happens, then Y. Coincidence?"',
        future_seed: '→ Plant seed + invitation. "What if tomorrow you tried X?"',
        gentle_support: '→ Warm support. "Even on hard days, X. That matters."',
        progress_highlight: '→ Show progress. "In [month] you were X. Now Y. Look how far."',
        milestone: `→ CELEBRATE! ${milestoneInfo.description || ''} "X days! Not luck, determination."`,
        relationship_insight: '→ Relationship insight + question. "With X, best moments are Y. When was the last time?"',
        memory_callback: `→ USE THIS MEMORY: ${memorySurprise.contrast || ''} Create past/present contrast. "I remember when you wrote X. Now Y."`
      },
      Spanish: {
        gentle_challenge: '→ Desafía + sugiere acción. "Dices X pero haces Y. ¿Y si probaras...?"',
        hidden_truth: '→ Verdad incómoda con amor. Termina con pregunta.',
        pattern_observation: '→ Muestra patrón + sugiere. "Noto que X. ¿Has probado Y?"',
        celebration: '→ Celebra + refuerza. "¡Hiciste X! ¿Cómo mantenerlo?"',
        connection: '→ Conecta + pregunta. "Cuando X, luego Y. ¿Coincidencia?"',
        future_seed: '→ Planta semilla. "¿Y si mañana probaras X?"',
        gentle_support: '→ Apoyo cálido. "Incluso en días difíciles, X. Eso importa."',
        progress_highlight: '→ Muestra progreso. "En [mes] estabas X. Ahora Y."',
        milestone: `→ ¡CELEBRA! ${milestoneInfo.description || ''}`,
        relationship_insight: '→ Insight relación + pregunta. "Con X, los mejores momentos son Y."',
        memory_callback: `→ USA ESTA MEMORIA: ${memorySurprise.contrast || ''} Contrasta pasado/presente.`
      },
      German: {
        gentle_challenge: '→ Hinterfrage + schlage vor. "Du sagst X, aber tust Y. Was wäre wenn...?"',
        hidden_truth: '→ Unbequeme Wahrheit mit Liebe. Ende mit Frage.',
        pattern_observation: '→ Zeige Muster + Vorschlag. "Mir fällt auf X. Hast du Y probiert?"',
        celebration: '→ Feiere + verstärke. "Du hast X gemacht! Wie kannst du das halten?"',
        connection: '→ Verbinde + Frage. "Wenn X, dann Y. Zufall?"',
        future_seed: '→ Pflanze Samen. "Was wäre, wenn du morgen X probierst?"',
        gentle_support: '→ Warme Unterstützung. "Auch an schweren Tagen, X. Das zählt."',
        progress_highlight: '→ Zeige Fortschritt. "Im [Monat] warst du X. Jetzt Y."',
        milestone: `→ FEIERE! ${milestoneInfo.description || ''} "X Tage! Nicht Glück, Entschlossenheit."`,
        relationship_insight: '→ Beziehungs-Insight + Frage. "Mit X sind die besten Momente Y."',
        memory_callback: `→ NUTZE DIESE ERINNERUNG: ${memorySurprise.contrast || ''} Vergangenheit/Gegenwart Kontrast.`
      },
      French: {
        gentle_challenge: '→ Questionne + suggère. "Tu dis X mais fais Y. Et si tu essayais...?"',
        hidden_truth: '→ Vérité inconfortable avec amour. Termine par question.',
        pattern_observation: '→ Montre pattern + suggère. "Je remarque X. As-tu essayé Y?"',
        celebration: '→ Célèbre + renforce. "Tu as fait X! Comment le maintenir?"',
        connection: '→ Connecte + question. "Quand X, puis Y. Coïncidence?"',
        future_seed: '→ Plante graine. "Et si demain tu essayais X?"',
        gentle_support: '→ Soutien chaleureux. "Même les jours difficiles, X. Ça compte."',
        progress_highlight: '→ Montre progrès. "En [mois] tu étais X. Maintenant Y."',
        milestone: `→ CÉLÈBRE! ${milestoneInfo.description || ''} "X jours! Pas de chance, détermination."`,
        relationship_insight: '→ Insight relation + question. "Avec X, les meilleurs moments sont Y."',
        memory_callback: `→ UTILISE CE SOUVENIR: ${memorySurprise.contrast || ''} Contraste passé/présent.`
      }
    };

    const prompts: Record<Language, string> = {
      Italian: `SEI UN AMICO CHE CONOSCE ${userName} DA ANNI.
Il tuo compito è generare UN insight che colpisca come un fulmine.

CONTESTO PROFONDO:
${personalizedContext}
${milestoneContext}${memoryContext}

VALORI DICHIARATI: ${valuesStr}
OBIETTIVI: ${goalsStr}
PATTERN COMPORTAMENTALI: ${patternsStr}
${contradictionsHints.length > 0 ? `CONTRADDIZIONI RILEVATE: ${contradictionsHints.join('; ')}` : ''}

${dynamicTone}

TIPO DI INSIGHT DA GENERARE: ${selectedType}
${typeHints.Italian[selectedType] || ''}

REGOLE FERREE:
1. MAX 20 parole - ogni parola deve pesare
2. ZERO frasi fatte ("Ogni passo conta", "Sei sulla strada giusta" = VIETATE)
3. Deve sembrare detto da qualcuno che ti conosce davvero
4. USA NOMI REALI di persone/luoghi se rilevanti
5. Il messaggio deve far pensare "wow, è proprio vero"
6. ❤️ PERSONE AMATE (moglie/marito/partner/figli): MAI interpretarle come "prigioni" o problemi. Se l'utente è preoccupato PER loro, è AMORE, non conflitto.
7. ⛔ MAI INVENTARE NUMERI DI GIORNI/SETTIMANE ("44 giorni", "33 giorni" = VIETATO). Non contare i giorni a meno che non sia indicato esplicitamente in MILESTONE DETECTED.
${avoidList}

ESEMPI PER TIPO:
- gentle_support: "Anche nei giorni pesanti, sei qui. Questo conta."
- milestone: "7 giorni consecutivi! Non è fortuna, è determinazione."
- progress_highlight: "A settembre eri perso. Ora guida tu."
- relationship_insight: "I tuoi momenti migliori? Sempre con Patricia accanto."
- celebration: "Tre settimane di palestra. Il tuo corpo ti ringrazia."
- connection: "Quando corri, poi scrivi meglio. Coincidenza?"

GENERA SOLO L'INSIGHT, NIENT'ALTRO:`,

      English: `YOU ARE A FRIEND WHO HAS KNOWN ${userName} FOR YEARS.
Your job is to generate ONE insight that hits like lightning.

DEEP CONTEXT:
${personalizedContext}
${milestoneContext}${memoryContext}

DECLARED VALUES: ${valuesStr}
GOALS: ${goalsStr}
BEHAVIORAL PATTERNS: ${patternsStr}
${contradictionsHints.length > 0 ? `DETECTED CONTRADICTIONS: ${contradictionsHints.join('; ')}` : ''}

${dynamicTone}

INSIGHT TYPE TO GENERATE: ${selectedType}
${typeHints.English[selectedType] || ''}

STRICT RULES:
1. MAX 20 words - every word must carry weight
2. ZERO clichés ("Every step counts", "You're on the right track" = FORBIDDEN)
3. Must sound like it's from someone who truly knows them
4. USE REAL NAMES of people/places if relevant
5. Message must make them think "wow, that's so true"
6. ❤️ LOVED ONES (spouse/partner/children): NEVER interpret them as "prisons" or problems.
7. ⛔ NEVER INVENT DAY/WEEK COUNTS ("44 days", "33 days" = FORBIDDEN). Don't count days unless explicitly stated in MILESTONE DETECTED.
${avoidList}

EXAMPLES BY TYPE:
- gentle_support: "Even on heavy days, you're here. That matters."
- milestone: "7 days straight! Not luck, determination."
- progress_highlight: "In September you were lost. Now you lead."
- relationship_insight: "Your best moments? Always with Patricia."
- celebration: "Three weeks at the gym. Your body thanks you."

GENERATE ONLY THE INSIGHT, NOTHING ELSE:`,

      Spanish: `ERES UN AMIGO QUE CONOCE A ${userName} DESDE HACE AÑOS.
Tu trabajo es generar UN insight que impacte como un rayo.

CONTEXTO PROFUNDO:
${personalizedContext}
${milestoneContext}${memoryContext}

VALORES DECLARADOS: ${valuesStr}
OBJETIVOS: ${goalsStr}
PATRONES DE COMPORTAMIENTO: ${patternsStr}
${contradictionsHints.length > 0 ? `CONTRADICCIONES DETECTADAS: ${contradictionsHints.join('; ')}` : ''}

${dynamicTone}

TIPO DE INSIGHT A GENERAR: ${selectedType}
${typeHints.Spanish[selectedType] || ''}

REGLAS ESTRICTAS:
1. MÁXIMO 20 palabras - cada palabra debe pesar
2. CERO frases hechas ("Cada paso cuenta", "Vas por buen camino" = PROHIBIDAS)
3. Debe sonar como alguien que realmente te conoce
4. USA NOMBRES REALES de personas/lugares si son relevantes
5. ❤️ SERES QUERIDOS: NUNCA interpretarlos como "prisiones" o problemas.
6. ⛔ NUNCA INVENTAR CONTEOS DE DÍAS ("44 días", "33 días" = PROHIBIDO). No cuentes días a menos que esté en MILESTONE DETECTED.
${avoidList}

GENERA SOLO EL INSIGHT, NADA MÁS:`,

      German: `DU BIST EIN FREUND, DER ${userName} SEIT JAHREN KENNT.
Deine Aufgabe ist es, EINE Erkenntnis zu generieren, die wie ein Blitz einschlägt.

TIEFER KONTEXT:
${personalizedContext}
${milestoneContext}${memoryContext}

ERKLÄRTE WERTE: ${valuesStr}
ZIELE: ${goalsStr}
VERHALTENSMUSTER: ${patternsStr}
${contradictionsHints.length > 0 ? `ERKANNTE WIDERSPRÜCHE: ${contradictionsHints.join('; ')}` : ''}

${dynamicTone}

ART DER ERKENNTNIS: ${selectedType}
${typeHints.German[selectedType] || ''}

STRIKTE REGELN:
1. MAXIMAL 20 Wörter - jedes Wort muss Gewicht haben
2. KEINE Floskeln ("Jeder Schritt zählt" = VERBOTEN)
3. Muss klingen wie von jemandem, der sie wirklich kennt
4. BENUTZE ECHTE NAMEN von Personen/Orten wenn relevant
5. ❤️ GELIEBTE: NIE als "Gefängnisse" oder Probleme interpretieren.
6. ⛔ NIE TAGE ERFINDEN ("44 Tage", "33 Tage" = VERBOTEN). Keine Tageszählung außer in MILESTONE DETECTED.
${avoidList}

GENERIERE NUR DIE ERKENNTNIS, NICHTS ANDERES:`,

      French: `TU ES UN AMI QUI CONNAÎT ${userName} DEPUIS DES ANNÉES.
Ton travail est de générer UN insight qui frappe comme la foudre.

CONTEXTE PROFOND:
${personalizedContext}
${milestoneContext}${memoryContext}

VALEURS DÉCLARÉES: ${valuesStr}
OBJECTIFS: ${goalsStr}
SCHÉMAS COMPORTEMENTAUX: ${patternsStr}
${contradictionsHints.length > 0 ? `CONTRADICTIONS DÉTECTÉES: ${contradictionsHints.join('; ')}` : ''}

${dynamicTone}

TYPE D'INSIGHT À GÉNÉRER: ${selectedType}
${typeHints.French[selectedType] || ''}

RÈGLES STRICTES:
1. MAXIMUM 20 mots - chaque mot doit avoir du poids
2. ZÉRO clichés ("Chaque pas compte" = INTERDITS)
3. Doit ressembler à quelqu'un qui les connaît vraiment
4. UTILISE LES VRAIS NOMS de personnes/lieux si pertinent
5. ❤️ ÊTRES CHERS: JAMAIS les interpréter comme des "prisons" ou problèmes.
6. ⛔ JAMAIS INVENTER DES COMPTAGES DE JOURS ("44 jours", "33 jours" = INTERDIT). Pas de comptage sauf si dans MILESTONE DETECTED.
${avoidList}

GÉNÈRE UNIQUEMENT L'INSIGHT, RIEN D'AUTRE:`
    };

    return prompts[lang] || prompts.English;
  };

  const deepInsightPrompt = getDeepInsightPrompt(context.language);

  // Add personality and feedback preferences to prompt if available
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);
  const feedbackSection = feedback ? buildFeedbackPromptSection(feedback, context.language) : '';
  const finalPrompt = personalitySection + deepInsightPrompt + feedbackSection;

  // User prompt in the user's language
  const userPrompt: Record<Language, string> = {
    Italian: 'Genera insight profondo.',
    English: 'Generate deep insight.',
    Spanish: 'Genera insight profundo.',
    German: 'Generiere tiefe Erkenntnis.',
    French: 'Génère un insight profond.'
  };

  try {
    const result = await aiChatCompletion(
      [
        { role: 'system', content: finalPrompt },
        { role: 'user', content: userPrompt[context.language] || userPrompt.English }
      ],
      { maxTokens: 100, temperature: 0.9 } // Higher temperature for variety
    );
    const insight = (result || pi.fallback).replace(/"/g, '').trim();

    // Save to history to avoid repetition
    addToInsightHistory(insight, 'pulse');

    // Build enhanced response with memory link if available
    const enhancedResult: EnhancedInsight = {
      text: insight,
      insightType: selectedType,
      emotionalContext: emotionalPulse.recentShift || 'stable'
    };

    // Add memory link if this was a memory_callback insight with a found memory
    if (selectedType === 'memory_callback' && memorySurprise.found && memorySurprise.oldEntry) {
      enhancedResult.memoryLink = {
        date: memorySurprise.formattedDate || '',
        preview: memorySurprise.oldEntry.moment?.substring(0, 80) || '',
        entryId: memorySurprise.oldEntry.id
      };
    }

    return enhancedResult;
  } catch {
    return {
      text: pi.defaultMessage,
      insightType: 'gentle_support' as InsightType,
      emotionalContext: 'fallback'
    };
  }
};

export const generateProactiveQuestion = async (
  context: LifeContext,
  entries: JournalEntry[],
  patterns: Pattern[] = [],
  feedback?: UserFeedbackContext
): Promise<{
  question: string;
  reason: string;
  relatedEntryId?: string;
  type: 'follow_up' | 'pattern_check' | 'goal_progress' | 'reflection' | 'deep_dive' | 'accountability' | 'celebration' | 'future';
}> => {
  const prompts = getPrompts(context.language);
  const pq = prompts.proactiveQuestion;
  const safeEntries = Array.isArray(entries) ? entries : [];

  const recentImportant = safeEntries
    .filter(e => {
      const daysSince = (Date.now() - e.timestamp) / (1000 * 60 * 60 * 24);
      return daysSince <= 7 && (e.goalAlignment > 70 || e.alignment > 70);
    })
    .slice(0, 3);

  // Find entries with strong emotions (positive or negative)
  const emotionalEntries = safeEntries
    .filter(e => {
      const daysSince = (Date.now() - e.timestamp) / (1000 * 60 * 60 * 24);
      const strongEmotions = ['Felice', 'Happy', 'Triste', 'Sad', 'Ansioso', 'Anxious', 'Arrabbiato', 'Angry', 'Grato', 'Grateful'];
      return daysSince <= 14 && strongEmotions.some(em => e.emotion?.includes(em));
    })
    .slice(0, 2);

  // Find entries mentioning people
  const entriesWithPeople = safeEntries
    .filter(e => e.people && e.people.length > 0)
    .slice(0, 3);

  const daysSinceLastEntry = safeEntries.length > 0
    ? Math.floor((Date.now() - safeEntries[0].timestamp) / (1000 * 60 * 60 * 24))
    : 999;

  const hasGoals = context.goals && context.goals.length > 0;
  const topGoal = hasGoals ? context.goals[0] : null;
  const blockers = context.blockers || [];

  // Get recent questions to avoid repetition
  const recentQuestions = getRecentInsights('question');
  const avoidList = recentQuestions.length > 0
    ? `\n⛔ NON RIPETERE QUESTE DOMANDE (già fatte di recente):\n${recentQuestions.slice(-5).map(q => `- "${q}"`).join('\n')}`
    : '';

  // Expanded question types for variety
  const questionTypes = [
    'follow_up',      // Su un momento recente specifico
    'pattern_check',  // Su un pattern identificato
    'goal_progress',  // Sui progressi verso obiettivi
    'reflection',     // Domanda riflessiva generale
    'deep_dive',      // Scava più a fondo su qualcosa di specifico
    'accountability', // Tieni responsabile l'utente su qualcosa
    'celebration',    // Celebra un traguardo o momento positivo
    'future'          // Domanda sul futuro/aspirazioni
  ];

  // Weighted random selection based on context
  let selectedType: string;
  const rand = Math.random();
  if (emotionalEntries.length > 0 && rand < 0.3) {
    selectedType = 'deep_dive';
  } else if (blockers.length > 0 && rand < 0.4) {
    selectedType = 'accountability';
  } else if (patterns.filter(p => p.isPositive).length > 0 && rand < 0.5) {
    selectedType = 'celebration';
  } else {
    selectedType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
  }

  // Deep question prompt - multilingual support for all 5 languages
  const getDeepQuestionPrompt = (lang: Language): string => {
    const userName = context.userName || (lang === 'Italian' ? 'questa persona' : lang === 'Spanish' ? 'esta persona' : lang === 'German' ? 'diese Person' : lang === 'French' ? 'cette personne' : 'this person');
    const topGoalStr = topGoal || (lang === 'Italian' ? 'non definito' : lang === 'Spanish' ? 'no definido' : lang === 'German' ? 'nicht definiert' : lang === 'French' ? 'non défini' : 'not defined');
    const blockersStr = blockers.join(', ') || (lang === 'Italian' ? 'nessuno' : lang === 'Spanish' ? 'ninguno' : lang === 'German' ? 'keine' : lang === 'French' ? 'aucun' : 'none');

    // Labels for context section
    const labels: Record<Language, { days: string; goal: string; moments: string; patterns: string; blockers: string; recentEntries: string; emotionalEntries: string; peopleEntries: string; patternsLabel: string; noEntries: string; none: string; analyzing: string }> = {
      Italian: { days: 'Giorni dall\'ultima entry', goal: 'Obiettivo principale', moments: 'Totale momenti', patterns: 'Pattern', blockers: 'Blocchi attivi', recentEntries: 'ENTRY RECENTI SIGNIFICATIVE', emotionalEntries: 'ENTRY CON EMOZIONI FORTI', peopleEntries: 'ENTRY CON PERSONE', patternsLabel: 'PATTERN', noEntries: 'Nessuna entry recente', none: 'Nessuna', analyzing: 'In analisi' },
      English: { days: 'Days since last entry', goal: 'Main goal', moments: 'Total moments', patterns: 'Patterns', blockers: 'Active blockers', recentEntries: 'RECENT SIGNIFICANT ENTRIES', emotionalEntries: 'ENTRIES WITH STRONG EMOTIONS', peopleEntries: 'ENTRIES WITH PEOPLE', patternsLabel: 'PATTERNS', noEntries: 'No recent entries', none: 'None', analyzing: 'Analyzing' },
      Spanish: { days: 'Días desde la última entrada', goal: 'Objetivo principal', moments: 'Total de momentos', patterns: 'Patrones', blockers: 'Bloqueos activos', recentEntries: 'ENTRADAS RECIENTES SIGNIFICATIVAS', emotionalEntries: 'ENTRADAS CON EMOCIONES FUERTES', peopleEntries: 'ENTRADAS CON PERSONAS', patternsLabel: 'PATRONES', noEntries: 'Sin entradas recientes', none: 'Ninguna', analyzing: 'Analizando' },
      German: { days: 'Tage seit letztem Eintrag', goal: 'Hauptziel', moments: 'Gesamte Momente', patterns: 'Muster', blockers: 'Aktive Blocker', recentEntries: 'WICHTIGE AKTUELLE EINTRÄGE', emotionalEntries: 'EINTRÄGE MIT STARKEN EMOTIONEN', peopleEntries: 'EINTRÄGE MIT PERSONEN', patternsLabel: 'MUSTER', noEntries: 'Keine aktuellen Einträge', none: 'Keine', analyzing: 'Analysieren' },
      French: { days: 'Jours depuis la dernière entrée', goal: 'Objectif principal', moments: 'Total des moments', patterns: 'Schémas', blockers: 'Blocages actifs', recentEntries: 'ENTRÉES RÉCENTES SIGNIFICATIVES', emotionalEntries: 'ENTRÉES AVEC ÉMOTIONS FORTES', peopleEntries: 'ENTRÉES AVEC DES PERSONNES', patternsLabel: 'SCHÉMAS', noEntries: 'Pas d\'entrées récentes', none: 'Aucune', analyzing: 'En analyse' }
    };

    const l = labels[lang] || labels.English;

    const typeHints: Record<Language, Record<string, string>> = {
      Italian: {
        follow_up: '→ Riprendi un momento specifico recente',
        pattern_check: '→ Chiedi di un pattern che hai notato',
        goal_progress: '→ Chiedi progressi su un obiettivo specifico',
        reflection: '→ Domanda riflessiva profonda',
        deep_dive: '→ Scava più a fondo su un emozione/evento specifico',
        accountability: '→ Tieni responsabile su un blocco o impegno',
        celebration: '→ Celebra qualcosa di positivo recente',
        future: '→ Domanda su aspirazioni/futuro'
      },
      English: {
        follow_up: '→ Follow up on a specific recent moment',
        pattern_check: '→ Ask about a pattern you noticed',
        goal_progress: '→ Ask about progress on a specific goal',
        reflection: '→ Deep reflective question',
        deep_dive: '→ Dig deeper into a specific emotion/event',
        accountability: '→ Hold accountable on a blocker or commitment',
        celebration: '→ Celebrate something positive recently',
        future: '→ Question about aspirations/future'
      },
      Spanish: {
        follow_up: '→ Retoma un momento específico reciente',
        pattern_check: '→ Pregunta sobre un patrón que notaste',
        goal_progress: '→ Pregunta sobre el progreso de un objetivo específico',
        reflection: '→ Pregunta reflexiva profunda',
        deep_dive: '→ Profundiza en una emoción/evento específico',
        accountability: '→ Responsabiliza sobre un bloqueo o compromiso',
        celebration: '→ Celebra algo positivo reciente',
        future: '→ Pregunta sobre aspiraciones/futuro'
      },
      German: {
        follow_up: '→ Knüpfe an einen bestimmten aktuellen Moment an',
        pattern_check: '→ Frage nach einem Muster, das dir aufgefallen ist',
        goal_progress: '→ Frage nach dem Fortschritt bei einem bestimmten Ziel',
        reflection: '→ Tiefe reflexive Frage',
        deep_dive: '→ Gehe tiefer auf eine bestimmte Emotion/Ereignis ein',
        accountability: '→ Halte verantwortlich für einen Blocker oder Verpflichtung',
        celebration: '→ Feiere etwas Positives aus der letzten Zeit',
        future: '→ Frage zu Bestrebungen/Zukunft'
      },
      French: {
        follow_up: '→ Reprends un moment spécifique récent',
        pattern_check: '→ Demande à propos d\'un schéma que tu as remarqué',
        goal_progress: '→ Demande les progrès sur un objectif spécifique',
        reflection: '→ Question réflexive profonde',
        deep_dive: '→ Creuse plus profondément une émotion/événement spécifique',
        accountability: '→ Tiens responsable sur un blocage ou engagement',
        celebration: '→ Célèbre quelque chose de positif récemment',
        future: '→ Question sur aspirations/avenir'
      }
    };

    const recentEntriesStr = recentImportant.map(e => `- [ID:${e.id}] ${new Date(e.timestamp).toLocaleDateString()}: "${e.content.substring(0, 100)}..." (${e.category}, ${lang === 'Italian' ? 'emozione' : lang === 'Spanish' ? 'emoción' : lang === 'German' ? 'Emotion' : lang === 'French' ? 'émotion' : 'emotion'}: ${e.emotion})`).join('\n') || l.noEntries;
    const emotionalStr = emotionalEntries.map(e => `- "${e.content.substring(0, 80)}..." (${e.emotion})`).join('\n') || l.none;
    const peopleStr = entriesWithPeople.map(e => `- "${e.content.substring(0, 80)}..." ${lang === 'Italian' ? 'con' : lang === 'Spanish' ? 'con' : lang === 'German' ? 'mit' : lang === 'French' ? 'avec' : 'with'} ${e.people?.map(p => p.name).join(', ')}`).join('\n') || l.none;
    const patternsListStr = patterns.slice(0, 4).map(p => `- ${p.isPositive ? '✅' : '⚠️'} ${p.title}: ${p.insight || p.description}`).join('\n') || l.analyzing;

    const prompts: Record<Language, string> = {
      Italian: `SEI UN AMICO FIDATO CHE CONOSCE ${userName} DA TEMPO.
Il tuo compito è fare UNA domanda che mostri che RICORDI e che ti INTERESSA davvero.

CONTESTO:
- ${l.days}: ${daysSinceLastEntry}
- ${l.goal}: ${topGoalStr}
- ${l.moments}: ${safeEntries.length}
- ${l.patterns}: ${patterns.length}
- ${l.blockers}: ${blockersStr}

${l.recentEntries}:
${recentEntriesStr}

${l.emotionalEntries}:
${emotionalStr}

${l.peopleEntries}:
${peopleStr}

${l.patternsLabel}:
${patternsListStr}

TIPO DI DOMANDA DA GENERARE: ${selectedType}
${typeHints.Italian[selectedType] || ''}

⛔ REGOLE ANTI-ALLUCINAZIONE (CRITICHE):
1. USA SOLO le informazioni presenti sopra - MAI INVENTARE dettagli
2. SOGNI ≠ REALTÀ: Se l'utente dice "sogno di avere bambini" NON assumere che abbia già bambini
3. ASPIRAZIONI ≠ FATTI: "Voglio imparare il piano" NON significa che sa suonare
4. NON INVENTARE viaggi, eventi, persone, bambini o dettagli non menzionati esplicitamente
5. Se non hai info concrete su un argomento, FAI una domanda APERTA, non assumere fatti
6. VERIFICA: Prima di citare qualcosa, assicurati che sia REALMENTE nelle entry sopra

REGOLE:
1. MAX 12 parole per la domanda
2. CITA SOLO cose che appaiono ESPLICITAMENTE nel contesto sopra
3. Mostra che RICORDI - "Mi avevi detto che...", "Quella volta che..."
4. Sii CURIOSO, non invadente
5. La domanda deve far sentire l'utente VISTO e COMPRESO
${avoidList}

ESEMPI DI ALLUCINAZIONI DA EVITARE:
❌ Se dice "sogno di avere bambini" → NON chiedere "come è andato il viaggio con i bambini?"
❌ Se dice "vorrei imparare a cucinare" → NON chiedere "cosa hai cucinato ieri?"
❌ Se menziona un amico una volta → NON inventare attività con quell'amico

ESEMPI BUONI (basati su fatti reali nelle entry):
✅ "Come è andata poi con Patricia dopo quel discorso?" (SOLO se c'è stata una entry su Patricia)
✅ "Hai ripreso a correre dopo l'infortunio?" (SOLO se hai info su infortunio nelle entry)

Rispondi SOLO in JSON:
{
  "question": "domanda diretta e personale",
  "reason": "perché chiedi - cita un fatto specifico REALE dalle entry sopra",
  "relatedEntryId": "id entry se rilevante, altrimenti null",
  "type": "${selectedType}"
}`,

      English: `YOU ARE A TRUSTED FRIEND WHO HAS KNOWN ${userName} FOR A WHILE.
Your job is to ask ONE question that shows you REMEMBER and genuinely CARE.

CONTEXT:
- ${l.days}: ${daysSinceLastEntry}
- ${l.goal}: ${topGoalStr}
- ${l.moments}: ${safeEntries.length}
- ${l.patterns}: ${patterns.length}
- ${l.blockers}: ${blockersStr}

${l.recentEntries}:
${recentEntriesStr}

${l.emotionalEntries}:
${emotionalStr}

${l.peopleEntries}:
${peopleStr}

${l.patternsLabel}:
${patternsListStr}

QUESTION TYPE TO GENERATE: ${selectedType}
${typeHints.English[selectedType] || ''}

⛔ ANTI-HALLUCINATION RULES (CRITICAL):
1. USE ONLY the information above - NEVER INVENT details
2. DREAMS ≠ REALITY: If user says "I dream of having kids" DO NOT assume they have kids
3. ASPIRATIONS ≠ FACTS: "I want to learn piano" does NOT mean they play piano
4. NEVER INVENT trips, events, people, children or details not explicitly mentioned
5. If you have no concrete info on a topic, ASK an OPEN question, don't assume facts
6. VERIFY: Before citing something, ensure it ACTUALLY exists in the entries above

RULES:
1. MAX 12 words for the question
2. CITE ONLY things that appear EXPLICITLY in the context above
3. Show you REMEMBER - "You mentioned that...", "That time when..."
4. Be CURIOUS, not intrusive
5. Question should make user feel SEEN and UNDERSTOOD
${avoidList}

HALLUCINATION EXAMPLES TO AVOID:
❌ If they say "I dream of having kids" → DON'T ask "how was the trip with the kids?"
❌ If they say "I want to learn to cook" → DON'T ask "what did you cook yesterday?"
❌ If they mention a friend once → DON'T invent activities with that friend

GOOD EXAMPLES (based on REAL facts from entries):
✅ "How did things go with Patricia after that talk?" (ONLY if there was an entry about Patricia)
✅ "Did you get back to running after the injury?" (ONLY if you have injury info in entries)

Respond ONLY in JSON:
{
  "question": "direct and personal question",
  "reason": "why you're asking - cite a REAL specific fact from entries above",
  "relatedEntryId": "entry id if relevant, otherwise null",
  "type": "${selectedType}"
}`,

      Spanish: `ERES UN AMIGO DE CONFIANZA QUE CONOCE A ${userName} DESDE HACE TIEMPO.
Tu trabajo es hacer UNA pregunta que muestre que RECUERDAS y que REALMENTE TE IMPORTA.

CONTEXTO:
- ${l.days}: ${daysSinceLastEntry}
- ${l.goal}: ${topGoalStr}
- ${l.moments}: ${safeEntries.length}
- ${l.patterns}: ${patterns.length}
- ${l.blockers}: ${blockersStr}

${l.recentEntries}:
${recentEntriesStr}

${l.emotionalEntries}:
${emotionalStr}

${l.peopleEntries}:
${peopleStr}

${l.patternsLabel}:
${patternsListStr}

TIPO DE PREGUNTA A GENERAR: ${selectedType}
${typeHints.Spanish[selectedType] || ''}

⛔ REGLAS ANTI-ALUCINACIÓN (CRÍTICAS):
1. USA SOLO la información de arriba - NUNCA INVENTES detalles
2. SUEÑOS ≠ REALIDAD: Si el usuario dice "sueño con tener hijos" NO asumas que tiene hijos
3. ASPIRACIONES ≠ HECHOS: "Quiero aprender piano" NO significa que sabe tocar
4. NUNCA INVENTES viajes, eventos, personas, niños o detalles no mencionados explícitamente
5. Si no tienes info concreta, HAZ una pregunta ABIERTA, no asumas hechos
6. VERIFICA: Antes de citar algo, asegúrate que REALMENTE existe en las entradas

REGLAS:
1. MÁXIMO 12 palabras para la pregunta
2. CITA SOLO cosas que aparecen EXPLÍCITAMENTE en el contexto
3. Muestra que RECUERDAS - "Me habías dicho que...", "Esa vez que..."
4. Sé CURIOSO, no invasivo
5. La pregunta debe hacer sentir al usuario VISTO y COMPRENDIDO
${avoidList}

EJEMPLOS DE ALUCINACIONES A EVITAR:
❌ Si dice "sueño con tener hijos" → NO preguntes "¿cómo fue el viaje con los niños?"
❌ Si dice "quiero aprender a cocinar" → NO preguntes "¿qué cocinaste ayer?"

BUENOS EJEMPLOS (basados en hechos REALES de las entradas):
✅ "¿Cómo te fue con Patricia después de esa conversación?" (SOLO si hay entrada sobre Patricia)
✅ "¿Volviste a correr después de la lesión?" (SOLO si tienes info de lesión)

Responde SOLO en JSON:
{
  "question": "pregunta directa y personal",
  "reason": "por qué preguntas - cita un hecho REAL de las entradas",
  "relatedEntryId": "id de entrada si es relevante, sino null",
  "type": "${selectedType}"
}`,

      German: `DU BIST EIN VERTRAUTER FREUND, DER ${userName} SCHON LANGE KENNT.
Deine Aufgabe ist es, EINE Frage zu stellen, die zeigt, dass du dich ERINNERST und wirklich INTERESSIERT bist.

KONTEXT:
- ${l.days}: ${daysSinceLastEntry}
- ${l.goal}: ${topGoalStr}
- ${l.moments}: ${safeEntries.length}
- ${l.patterns}: ${patterns.length}
- ${l.blockers}: ${blockersStr}

${l.recentEntries}:
${recentEntriesStr}

${l.emotionalEntries}:
${emotionalStr}

${l.peopleEntries}:
${peopleStr}

${l.patternsLabel}:
${patternsListStr}

ART DER FRAGE ZU GENERIEREN: ${selectedType}
${typeHints.German[selectedType] || ''}

⛔ ANTI-HALLUZINATIONS-REGELN (KRITISCH):
1. BENUTZE NUR die Informationen oben - ERFINDE NIEMALS Details
2. TRÄUME ≠ REALITÄT: Wenn Benutzer sagt "Ich träume von Kindern" NIMM NICHT AN, dass er Kinder hat
3. WÜNSCHE ≠ FAKTEN: "Ich möchte Klavier lernen" bedeutet NICHT, dass er spielen kann
4. ERFINDE NIEMALS Reisen, Ereignisse, Personen, Kinder oder nicht explizit erwähnte Details
5. Wenn du keine konkreten Infos hast, STELLE eine OFFENE Frage, nimm keine Fakten an
6. VERIFIZIERE: Stelle sicher, dass etwas WIRKLICH in den Einträgen existiert

REGELN:
1. MAXIMAL 12 Wörter für die Frage
2. ZITIERE NUR Dinge, die EXPLIZIT im Kontext erscheinen
3. Zeige, dass du dich ERINNERST - "Du hattest mir erzählt, dass...", "Damals als..."
4. Sei NEUGIERIG, nicht aufdringlich
5. Die Frage sollte den Benutzer GESEHEN und VERSTANDEN fühlen lassen
${avoidList}

HALLUZINATIONSBEISPIELE ZU VERMEIDEN:
❌ Wenn er sagt "Ich träume von Kindern" → FRAGE NICHT "Wie war die Reise mit den Kindern?"
❌ Wenn er sagt "Ich möchte kochen lernen" → FRAGE NICHT "Was hast du gestern gekocht?"

GUTE BEISPIELE (basierend auf ECHTEN Fakten aus Einträgen):
✅ "Wie lief es mit Patricia nach dem Gespräch?" (NUR wenn es einen Eintrag über Patricia gibt)
✅ "Hast du nach der Verletzung wieder angefangen zu laufen?" (NUR wenn Verletzungsinfo vorhanden)

Antworte NUR in JSON:
{
  "question": "direkte und persönliche Frage",
  "reason": "warum du fragst - zitiere einen ECHTEN spezifischen Fakt",
  "relatedEntryId": "Eintrags-ID wenn relevant, sonst null",
  "type": "${selectedType}"
}`,

      French: `TU ES UN AMI DE CONFIANCE QUI CONNAÎT ${userName} DEPUIS LONGTEMPS.
Ton travail est de poser UNE question qui montre que tu te SOUVIENS et que tu t'INTÉRESSES vraiment.

CONTEXTE:
- ${l.days}: ${daysSinceLastEntry}
- ${l.goal}: ${topGoalStr}
- ${l.moments}: ${safeEntries.length}
- ${l.patterns}: ${patterns.length}
- ${l.blockers}: ${blockersStr}

${l.recentEntries}:
${recentEntriesStr}

${l.emotionalEntries}:
${emotionalStr}

${l.peopleEntries}:
${peopleStr}

${l.patternsLabel}:
${patternsListStr}

TYPE DE QUESTION À GÉNÉRER: ${selectedType}
${typeHints.French[selectedType] || ''}

⛔ RÈGLES ANTI-HALLUCINATION (CRITIQUES):
1. UTILISE UNIQUEMENT les infos ci-dessus - N'INVENTE JAMAIS de détails
2. RÊVES ≠ RÉALITÉ: Si l'utilisateur dit "je rêve d'avoir des enfants" N'ASSUME PAS qu'il a des enfants
3. ASPIRATIONS ≠ FAITS: "Je veux apprendre le piano" NE signifie PAS qu'il sait jouer
4. N'INVENTE JAMAIS voyages, événements, personnes, enfants ou détails non mentionnés
5. Si tu n'as pas d'info concrète, POSE une question OUVERTE, n'assume pas de faits
6. VÉRIFIE: Avant de citer quelque chose, assure-toi que c'est VRAIMENT dans les entrées

RÈGLES:
1. MAXIMUM 12 mots pour la question
2. CITE UNIQUEMENT des choses qui apparaissent EXPLICITEMENT dans le contexte
3. Montre que tu te SOUVIENS - "Tu m'avais dit que...", "Cette fois où..."
4. Sois CURIEUX, pas intrusif
5. La question doit faire sentir l'utilisateur VU et COMPRIS
${avoidList}

EXEMPLES D'HALLUCINATIONS À ÉVITER:
❌ S'il dit "je rêve d'avoir des enfants" → NE demande PAS "comment s'est passé le voyage avec les enfants?"
❌ S'il dit "je veux apprendre à cuisiner" → NE demande PAS "qu'as-tu cuisiné hier?"

BONS EXEMPLES (basés sur des faits RÉELS des entrées):
✅ "Comment ça s'est passé avec Patricia après cette discussion?" (SEULEMENT s'il y a une entrée sur Patricia)
✅ "Tu as repris la course après ta blessure?" (SEULEMENT si tu as info sur blessure)

Réponds UNIQUEMENT en JSON:
{
  "question": "question directe et personnelle",
  "reason": "pourquoi tu demandes - cite un fait RÉEL des entrées",
  "relatedEntryId": "id d'entrée si pertinent, sinon null",
  "type": "${selectedType}"
}`
    };

    return prompts[lang] || prompts.English;
  };

  const deepQuestionPrompt = getDeepQuestionPrompt(context.language);

  // Add personality and feedback preferences to prompt if available
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);
  const questionFeedbackSection = feedback ? buildFeedbackPromptSection(feedback, context.language) : '';
  const finalQuestionPrompt = personalitySection + deepQuestionPrompt + questionFeedbackSection;

  // User prompt in the user's language
  const questionUserPrompt: Record<Language, string> = {
    Italian: 'Genera domanda proattiva.',
    English: 'Generate proactive question.',
    Spanish: 'Genera pregunta proactiva.',
    German: 'Generiere proaktive Frage.',
    French: 'Génère une question proactive.'
  };

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: finalQuestionPrompt },
        { role: 'user', content: questionUserPrompt[context.language] || questionUserPrompt.English }
      ],
      { jsonMode: true, maxTokens: 300, temperature: 0.85 }
    );
    const result = JSON.parse(content || '{}');

    // Save to history to avoid repetition
    if (result.question) {
      addToInsightHistory(result.question, 'question');
    }

    return result;
  } catch {
    return {
      question: context.language === 'Italian' ? "Come stai oggi?" : "How are you today?",
      reason: context.language === 'Italian' ? "Volevo sapere come va" : "Checking in",
      type: 'reflection'
    };
  }
};

// Detect emotional tone from user message with weighted scoring (language-aware)
const detectEmotionalTone = (query: string, language: Language): 'struggle' | 'celebration' | 'neutral' | 'curiosity' => {
  const prompts = getPrompts(language);
  const lowerQuery = query.toLowerCase();
  
  const struggleIndicators = prompts.emotionalIndicators.struggle;
  const celebrationIndicators = prompts.emotionalIndicators.celebration;
  const curiosityIndicators = prompts.emotionalIndicators.curiosity;
  
  // Count matches (weighted approach)
  const struggleScore = struggleIndicators.filter(i => lowerQuery.includes(i)).length;
  const celebrationScore = celebrationIndicators.filter(i => lowerQuery.includes(i)).length;
  const curiosityScore = curiosityIndicators.filter(i => lowerQuery.includes(i)).length;
  
  // Mixed sentiment: celebration wins if user overcame, struggle if still needs support
  if (struggleScore > 0 && celebrationScore > 0) {
    if (celebrationScore >= struggleScore) return 'celebration';
    return 'struggle';
  }
  
  if (struggleScore > 0) return 'struggle';
  if (celebrationScore > 0) return 'celebration';
  if (curiosityScore > 0) return 'curiosity';
  return 'neutral';
};

// Get emotion-specific prompt modifiers (language-aware)
const getEmotionModePrompt = (emotionalTone: 'struggle' | 'celebration' | 'neutral' | 'curiosity', language: Language): string => {
  const prompts = getPrompts(language);
  return prompts.emotionalModes[emotionalTone];
};

// Chat message type for conversation history
interface ConversationMessage {
  role: 'user' | 'model';
  content: string;
}

// Cached conversation summary to avoid regenerating
interface ConversationSummary {
  themes: string[];
  peopleDiscussed: string[];
  emotionalJourney: string;
  keyInsights: string[];
  lastUpdatedAt: number;
}

// LOCAL summary extraction without extra API calls
const extractLocalConversationSummary = (
  conversationHistory: ConversationMessage[]
): ConversationSummary => {
  if (conversationHistory.length < 4) {
    return {
      themes: [],
      peopleDiscussed: [],
      emotionalJourney: '',
      keyInsights: [],
      lastUpdatedAt: Date.now()
    };
  }

  // Extract themes from conversation keywords locally (no API call)
  const allText = conversationHistory.map(m => m.content.toLowerCase()).join(' ');
  
  // Common themes detection (simple keyword matching)
  const themeKeywords: Record<string, string[]> = {
    'work': ['trabajo', 'work', 'proyecto', 'project', 'empleo', 'job'],
    'relationships': ['familia', 'family', 'amigo', 'friend', 'pareja', 'partner', 'amor', 'love'],
    'health': ['salud', 'health', 'ejercicio', 'exercise', 'dormir', 'sleep'],
    'goals': ['meta', 'goal', 'objetivo', 'objetivo', 'futuro', 'future'],
    'emotions': ['siento', 'feel', 'triste', 'sad', 'feliz', 'happy', 'ansiedad', 'anxiety']
  };
  
  const detectedThemes: string[] = [];
  for (const [theme, keywords] of Object.entries(themeKeywords)) {
    if (keywords.some(kw => allText.includes(kw))) {
      detectedThemes.push(theme);
    }
  }
  
  // Extract potential names (capitalized words that appear multiple times)
  const namePattern = /\b[A-Z][a-z]{2,}\b/g;
  const allContent = conversationHistory.map(m => m.content).join(' ');
  const potentialNames = allContent.match(namePattern) || [];
  const nameCounts = potentialNames.reduce((acc, name) => {
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const frequentNames = Object.entries(nameCounts)
    .filter(([_, count]) => count >= 2)
    .map(([name]) => name)
    .slice(0, 3);
  
  // Determine emotional journey from recent messages
  const recentUserMessages = conversationHistory
    .filter(m => m.role === 'user')
    .slice(-3)
    .map(m => m.content.substring(0, 50));
  const emotionalJourney = recentUserMessages.length > 0 
    ? 'Exploring: ' + recentUserMessages[recentUserMessages.length - 1].substring(0, 30) + '...'
    : '';
  
  return {
    themes: detectedThemes.slice(0, 3),
    peopleDiscussed: frequentNames,
    emotionalJourney,
    keyInsights: [],
    lastUpdatedAt: Date.now()
  };
};

// Build lightweight context for ongoing conversations (after first few messages)
// CRITICAL: Always includes real entries with actual dates AND key context elements for same value as full context
const buildLightweightContext = (
  context: LifeContext,
  summary: ConversationSummary,
  recentHistory: ConversationMessage[],
  relevantEntries: JournalEntry[] = [],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  enhancedContext?: any,
  patterns?: Array<{ title: string; isPositive: boolean }>,
  pendingDecisions?: Array<{ title: string }>
): string => {
  const recentMessages = recentHistory.slice(-4).map(m => 
    `${m.role === 'user' ? '👤' : '🤖'}: ${m.content.substring(0, 120)}${m.content.length > 120 ? '...' : ''}`
  ).join('\n');

  // CRITICAL: Include real entries with ACTUAL dates - this is the ONLY source of truth
  const entriesWithDates = relevantEntries.slice(0, 8).map(e => {
    const realDate = new Date(e.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `• [${e.id}] ${realDate}: "${e.content.substring(0, 50)}..." (${e.emotion || 'neutral'})`;
  }).join('\n');

  // Compact patterns summary
  const patternsStr = patterns && patterns.length > 0
    ? patterns.slice(0, 4).map(p => `${p.isPositive ? '✅' : '⚠️'} ${p.title}`).join(', ')
    : 'none yet';

  // Key people summary WITH emotional tone and frequency
  const peopleStr = enhancedContext?.keyPeople && enhancedContext.keyPeople.length > 0
    ? enhancedContext.keyPeople.slice(0, 3).map((p: any) => {
        const moodIcon = p.emotionalTone === 'positive' ? '💚' : p.emotionalTone === 'negative' ? '💔' : '⚪';
        return `${moodIcon} ${p.name} (${p.relationship}, ${p.frequency}x)`;
      }).join(', ')
    : summary.peopleDiscussed.join(', ') || 'none';
  
  // Key places with context
  const placesStr = enhancedContext?.keyPlaces && enhancedContext.keyPlaces.length > 0
    ? enhancedContext.keyPlaces.slice(0, 2).map((pl: any) => `${pl.name} (${pl.frequency}x)`).join(', ')
    : '';

  // Pending decisions
  const decisionsStr = pendingDecisions && pendingDecisions.length > 0
    ? pendingDecisions.slice(0, 2).map(d => d.title).join(', ')
    : 'none';

  // Emotional state
  const emotionalState = enhancedContext?.emotionalPulse
    ? `${enhancedContext.emotionalPulse.dominantEmotion} (${enhancedContext.emotionalPulse.emotionalIntensity})`
    : 'neutral';

  // Emotional triggers (top 3) - uses 'trigger' and 'isPositive' fields
  const triggersStr = enhancedContext?.frequentTriggers && enhancedContext.frequentTriggers.length > 0
    ? enhancedContext.frequentTriggers.slice(0, 3).map((t: any) => `${t.isPositive ? '✅' : '⚠️'} ${t.trigger}`).join(', ')
    : '';

  // Life dimensions trend (compact) - includes summary narrative
  let dimensionsStr = '';
  if (enhancedContext?.dimensionsTrend) {
    const dt = enhancedContext.dimensionsTrend;
    dimensionsStr = `Strong: ${dt.strongestArea} | Weak: ${dt.weakestArea}`;
    if (dt.summary) dimensionsStr += ` | "${dt.summary.substring(0, 60)}..."`;
  }

  // Category health (compact) - uses category, status, trend
  const categoryStr = enhancedContext?.categoryHealth && enhancedContext.categoryHealth.length > 0
    ? enhancedContext.categoryHealth.slice(0, 3).map((c: any) => `${c.category}:${c.status}`).join(' ')
    : '';

  // Temporal patterns - uses pattern field
  const temporalStr = enhancedContext?.temporalPatterns && enhancedContext.temporalPatterns.length > 0
    ? enhancedContext.temporalPatterns.slice(0, 2).map((t: any) => t.pattern).join('; ')
    : '';

  // Decision learnings - uses successRate, commonPatterns, and similar decisions with lessons
  let decisionStr = '';
  if (enhancedContext?.decisionLearning) {
    const dl = enhancedContext.decisionLearning;
    decisionStr = `Success rate: ${dl.successRate}%`;
    if (dl.similarDecisions && dl.similarDecisions.length > 0) {
      const lessons = dl.similarDecisions.slice(0, 2).map((d: any) => 
        `"${d.title}" → ${d.outcome}${d.lesson ? `: ${d.lesson.substring(0, 40)}` : ''}`
      ).join('; ');
      decisionStr += ` | Lessons: ${lessons}`;
    }
  }

  // Temporal cadence - best day/time for engagement
  let temporalCadenceStr = '';
  if (enhancedContext?.bestDayOfWeek || enhancedContext?.bestTimeOfDay) {
    temporalCadenceStr = `Best time: ${enhancedContext.bestTimeOfDay || '?'} on ${enhancedContext.bestDayOfWeek || '?'}`;
  }

  // Build compact but value-rich context matching full context quality
  const sections: string[] = [];
  
  // AI Profile summary (deep knowledge about user)
  const aiProfile = context.aiProfile as any;
  let profileStr = '';
  try {
    if (aiProfile) {
      const profileParts: string[] = [];
      if (aiProfile.summary && typeof aiProfile.summary === 'string') {
        profileParts.push(`"${aiProfile.summary.substring(0, 100)}..."`);
      }
      if (Array.isArray(aiProfile.personalityTraits) && aiProfile.personalityTraits.length > 0) {
        const traits = aiProfile.personalityTraits.slice(0, 3).map((t: any) => t?.trait).filter(Boolean).join(', ');
        if (traits) profileParts.push(`Traits: ${traits}`);
      }
      if (Array.isArray(aiProfile.coreValues) && aiProfile.coreValues.length > 0) {
        const values = aiProfile.coreValues.slice(0, 3).map((v: any) => v?.name).filter(Boolean).join(', ');
        if (values) profileParts.push(`Core values: ${values}`);
      }
      profileStr = profileParts.join(' | ');
    }
  } catch (e) {
    console.error('Error extracting AI profile for lightweight context:', e);
    profileStr = '';
  }
  
  // User identity WITH AI profile
  sections.push(`━━━ USER ━━━
${context.userName} | Values: ${context.values?.slice(0, 3).join(', ') || 'none'} | Goals: ${context.goals?.slice(0, 3).join(', ') || 'none'}
Blockers: ${context.blockers?.slice(0, 2).join(', ') || 'none'}
${profileStr ? `AI Profile: ${profileStr}` : ''}`);

  // Current state with all signals
  let stateLines = `Mood: ${enhancedContext?.currentMood || '?'} | Area: ${enhancedContext?.dominantCategory || '?'} | Trend: ${enhancedContext?.alignmentTrend || 'stable'}`;
  stateLines += `\nEmotion: ${emotionalState}`;
  if (dimensionsStr) stateLines += `\nDimensions: ${dimensionsStr}`;
  if (categoryStr) stateLines += `\nCategories: ${categoryStr}`;
  if (triggersStr) stateLines += `\nTriggers: ${triggersStr}`;
  if (temporalStr) stateLines += `\nPatterns: ${temporalStr}`;
  if (temporalCadenceStr) stateLines += `\n${temporalCadenceStr}`;
  sections.push(`━━━ STATE ━━━\n${stateLines}`);

  // Social and behavioral intelligence
  let behaviorLines = `Patterns: ${patternsStr}`;
  behaviorLines += `\nPeople: ${peopleStr}`;
  if (placesStr) behaviorLines += `\nPlaces: ${placesStr}`;
  behaviorLines += `\nPending decisions: ${decisionsStr}`;
  if (decisionStr) behaviorLines += `\nDecision intelligence: ${decisionStr}`;
  sections.push(`━━━ INTELLIGENCE ━━━\n${behaviorLines}`);

  // Entries with dates
  sections.push(`━━━ ENTRIES (CITE ONLY THESE) ━━━
${entriesWithDates || 'None - do not cite any'}
⚠️ Use EXACT dates above only.`);

  // Conversation context
  sections.push(`━━━ CONVERSATION ━━━
Themes: ${summary.themes.join(', ') || 'exploring'}
${recentMessages}`);

  return sections.join('\n\n');
};

// Generate context-aware AI response with specific citations
export const getContextAwareResponse = async (
  query: string,
  context: LifeContext,
  entries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[] = [],
  conversationHistory: ConversationMessage[] = [],
  cachedSessionContext?: ChatSessionContext | null,
  feedback?: UserFeedbackContext
): Promise<{
  response: string;
  citedEntries: Array<{ id: string; date: string; snippet: string }>;
  patternsUsed: string[];
  suggestions: string[];
  conversationSuggestions: string[];
  directActions: Array<{ type: string; title: string; reason: string }>;
  valuesUsed: string[];
  goalsUsed: string[];
  emotionalMode: 'struggle' | 'celebration' | 'neutral' | 'curiosity';
  responseType?: string;
  showSuggestions?: boolean;
  extractedEntities?: { people: string[]; places: string[]; decisions: string[] };
}> => {
  const prompts = getPrompts(context.language);
  const labels = prompts.contextLabels;
  const car = prompts.contextAwareResponse;
  const safeEntries = Array.isArray(entries) ? entries : [];
  const p = context.aiPersonality || { warmth: 80, directness: 50, depth: 50 };

  // ========================================
  // AI TIER SYSTEM - Adaptive Intelligence
  // ========================================
  const aiTier = cachedSessionContext?.aiTier || calculateAITier(safeEntries.length, patterns.length);
  const tierDescription = cachedSessionContext?.tierDescription || getAITierDescription(aiTier);
  const aiBehavior = getAIBehaviorForTier(aiTier, context.language);

  // ========================================
  // FRIEND MODE PROMPTS - New conversational style
  // ========================================
  const friendMode = cachedSessionContext?.friendModePrompts || getFriendModePrompts(context.language);

  // ========================================
  // CONTEXT LOADING - Use cached or build fresh
  // ========================================
  // If cachedSessionContext is provided, use it for efficiency
  // Otherwise build fresh (first message or fallback)
  const enhancedContext = cachedSessionContext?.enhancedContext
    ? cachedSessionContext.enhancedContext
    : await buildEnhancedContextWithSemantics(query, context, safeEntries, decisions, patterns, conversationHistory);

  // Detect emotional tone for dynamic response mode (language-aware)
  const emotionalTone = detectEmotionalTone(query, context.language);
  const emotionModePrompt = getEmotionModePrompt(emotionalTone, context.language);

  // Use weighted relevant entries instead of basic keyword match
  const relevantEntries = enhancedContext.topEntries.slice(0, 8).map(se => se.entry);

  // Get pending decisions
  const pendingDecisions = decisions.filter(d => d.status === 'pending').slice(0, 3);

  // Build conversation context from history
  const conversationContext = conversationHistory.length > 0 
    ? `\n\n━━━ ${car.conversationHistory} ━━━\n${conversationHistory.slice(-10).map(m => 
        `${m.role === 'user' ? `👤 ${labels.user}` : `🤖 ${labels.ai}`}: ${m.content.substring(0, 200)}${m.content.length > 200 ? '...' : ''}`
      ).join('\n')}\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`
    : '';

  // Extract entities mentioned in conversation for context enrichment
  const allConversationText = conversationHistory.map(m => m.content).join(' ') + ' ' + query;

  const allValues = context.values?.join(', ') || labels.none;
  const allGoals = context.goals?.join(', ') || labels.none;
  const allBlockers = context.blockers?.join(', ') || labels.none;

  // Build enhanced people context string
  const peopleContextStr = enhancedContext.keyPeople.length > 0 
    ? `\n\n🧑‍🤝‍🧑 KEY PEOPLE IN USER'S LIFE:\n${enhancedContext.keyPeople.slice(0, 5).map(p => {
        const moodIcon = p.emotionalTone === 'positive' ? '💚' : p.emotionalTone === 'negative' ? '💔' : p.emotionalTone === 'mixed' ? '💛' : '⚪';
        return `• ${p.name} (${p.relationship}) ${moodIcon} - mentioned ${p.frequency}x, mood impact: ${p.avgMoodImpact > 0 ? '+' : ''}${Math.round(p.avgMoodImpact)}`;
      }).join('\n')}`
    : '';

  // Build places context string
  const placesContextStr = enhancedContext.keyPlaces.length > 0
    ? `\n\n📍 SIGNIFICANT PLACES:\n${enhancedContext.keyPlaces.slice(0, 4).map(p => {
        const toneIcon = p.emotionalAssociation === 'positive' ? '✨' : p.emotionalAssociation === 'negative' ? '😔' : '➖';
        return `• ${p.name} (${p.type}) ${toneIcon} - ${p.frequency}x visits, alignment: ${p.avgGoalAlignment}%`;
      }).join('\n')}`
    : '';

  // Build temporal patterns string
  const bestTime = enhancedContext.bestTimeOfDay;
  const bestDay = enhancedContext.bestDayOfWeek;
  const temporalStr = (bestTime || bestDay) 
    ? `\n\n⏰ TEMPORAL INSIGHTS:\n${bestTime ? `• Best productivity time: ${bestTime}` : ''}${bestDay ? `\n• Best day: ${bestDay}` : ''}\n• Current trend: ${enhancedContext.alignmentTrend}`
    : '';

  // Build decision learning string
  const decisionLearningStr = enhancedContext.decisionLearning && enhancedContext.decisionLearning.similarDecisions.length > 0
    ? `\n\n📊 SIMILAR PAST DECISIONS (learn from history - success rate: ${enhancedContext.decisionLearning.successRate}%):\n${enhancedContext.decisionLearning.similarDecisions.slice(0, 3).map(d => {
        const outcomeIcon = d.outcome === 'positive' ? '✅' : d.outcome === 'negative' ? '❌' : '➖';
        return `• ${outcomeIcon} "${d.title}" → chose "${d.chosenOption}" (${d.outcome})${d.lesson ? ` - Lesson: ${d.lesson}` : ''}`;
      }).join('\n')}`
    : '';

  // === DEEP CONTEXT - Emotional Intelligence ===
  const pulse = enhancedContext.emotionalPulse;
  const emotionalPulseStr = `\n\n💜 EMOTIONAL STATE (CRITICAL - adapt your tone):
• Current emotional narrative: ${pulse.trendNarrative}
• Dominant emotion: ${pulse.dominantEmotion} | Intensity: ${pulse.emotionalIntensity} | Shift: ${pulse.recentShift}
• Weekly emotion distribution: ${Object.entries(pulse.distribution7d).map(([e, c]) => `${e}×${c}`).join(' ') || 'no data'}
${pulse.streakType ? `• Emotional streak: ${pulse.streakDays} days of ${pulse.streakType} emotions` : ''}`;

  // Life Dimensions Trend
  const dims = enhancedContext.dimensionsTrend;
  const dimTrendIcon = (t: string) => t === 'up' ? '📈' : t === 'down' ? '📉' : '➡️';
  const dimensionsStr = `\n\n🎯 LIFE DIMENSIONS (14-day trend):
• Health: ${dims.salute.total > 0 ? '+' : ''}${dims.salute.total} ${dimTrendIcon(dims.salute.trend)}
• Focus: ${dims.focus.total > 0 ? '+' : ''}${dims.focus.total} ${dimTrendIcon(dims.focus.trend)}
• Connection: ${dims.connessione.total > 0 ? '+' : ''}${dims.connessione.total} ${dimTrendIcon(dims.connessione.trend)}
• Spirit: ${dims.spirito.total > 0 ? '+' : ''}${dims.spirito.total} ${dimTrendIcon(dims.spirito.trend)}
• Finance: ${dims.finanza.total > 0 ? '+' : ''}${dims.finanza.total} ${dimTrendIcon(dims.finanza.trend)}
• STRONGEST: ${dims.strongestArea} | WEAKEST: ${dims.weakestArea}`;

  // Category Health
  const catHealth = enhancedContext.categoryHealth;
  const statusIcon = (s: string) => s === 'thriving' ? '🌟' : s === 'healthy' ? '✅' : s === 'needs_attention' ? '⚠️' : '🚨';
  const categoryHealthStr = catHealth.length > 0 
    ? `\n\n📊 CATEGORY HEALTH:\n${catHealth.slice(0, 5).map(c => 
        `• ${statusIcon(c.status)} ${c.category}: ${c.avgAlignment}% (${c.status}, trend: ${c.trend}) ${c.recentEmotions.join('')}`
      ).join('\n')}`
    : '';

  // Emotional Triggers
  const triggers = enhancedContext.frequentTriggers;
  const triggersStr = triggers.length > 0
    ? `\n\n⚡ KNOWN EMOTIONAL TRIGGERS (use carefully):\n${triggers.slice(0, 4).map(t => 
        `• "${t.trigger}" → ${t.isPositive ? '😊' : '😔'} (${t.frequency}x, impact: ${t.avgMoodImpact > 0 ? '+' : ''}${t.avgMoodImpact})`
      ).join('\n')}`
    : '';

  // Enhanced people context with recent moments
  const deepPeopleStr = enhancedContext.keyPeople.length > 0 
    ? `\n\n👥 PEOPLE IN USER'S LIFE (with recent interactions):\n${enhancedContext.keyPeople.slice(0, 5).map(p => {
        const moodIcon = p.emotionalTone === 'positive' ? '💚' : p.emotionalTone === 'negative' ? '💔' : p.emotionalTone === 'mixed' ? '💛' : '⚪';
        const recentMomentsStr = p.recentMoments.length > 0 ? `\n  Recent: "${p.recentMoments[0].substring(0, 60)}..."` : '';
        return `• ${p.name} (${p.relationship}) ${moodIcon} - ${p.frequency}x mentions, mood: ${p.avgMoodImpact > 0 ? '+' : ''}${Math.round(p.avgMoodImpact)}${recentMomentsStr}`;
      }).join('\n')}`
    : '';

  // Count how many messages in conversation to adjust behavior
  const messageCount = conversationHistory.length;
  const isEarlyConversation = messageCount < 4;
  const isDeepConversation = messageCount >= 6;
  
  // TOKEN OPTIMIZATION: Use lightweight context after many messages
  // Extended to 8 messages to ensure AI always has rich context for relevant responses
  const useFullContext = messageCount < 8; // Full context for first 8 exchanges (most conversations)
  
  // Extract conversation summary locally (no API call) for ongoing conversations
  let conversationSummaryData: ConversationSummary | null = null;
  if (!useFullContext && messageCount >= 4) {
    conversationSummaryData = extractLocalConversationSummary(conversationHistory);
  }

  // Detect short acknowledgments that don't need elaborate responses (language-aware)
  const isShortAcknowledgment = prompts.acknowledgments.includes(query.toLowerCase().trim()) || 
    (query.trim().length < 12 && !query.includes('?') && !query.includes('!'));

  // Build context section based on conversation stage
  const buildFullContextSection = (): string => {
    const entriesStr = relevantEntries.slice(0, 6).map((e, i) => {
      const score = enhancedContext.topEntries[i]?.score || 0;
      return '• [relevance: ' + Math.round(score) + '] ' + new Date(e.timestamp).toLocaleDateString() + ': "' + e.content.substring(0, 100) + '..."';
    }).join('\n') || labels.noRelevantEntries;
    
    const decisionsStr = pendingDecisions.map(d => '• ' + d.title).join('\n') || labels.noPendingDecisions;
    
    return car.contextIntro + '\n' +
      '• ' + labels.values + ': ' + allValues + '\n' +
      '• ' + labels.goals + ': ' + allGoals + '\n' +
      '• ' + labels.blockers + ': ' + allBlockers + '\n' +
      '• ' + labels.trackedMoments + ': ' + safeEntries.length + '\n' +
      '• ' + labels.patterns + ': ' + (patterns.map(pat => pat.title).join(', ') || labels.none) + '\n' +
      '• Current mood: ' + enhancedContext.currentMood + ' | Dominant area: ' + enhancedContext.dominantCategory + ' | Trend: ' + enhancedContext.alignmentTrend +
      emotionalPulseStr + dimensionsStr + categoryHealthStr + triggersStr + deepPeopleStr + placesContextStr + temporalStr + decisionLearningStr + '\n\n' +
      labels.recentEntries + ' (WEIGHTED BY RELEVANCE - use these to personalize):\n' + entriesStr + '\n\n' +
      labels.pendingDecisions + ':\n' + decisionsStr +
      conversationContext;
  };
  
  const buildSimpleContext = (): string => {
    const recentMsgs = conversationHistory.slice(-4).map(m => 
      (m.role === 'user' ? '👤' : '🤖') + ': ' + m.content.substring(0, 150) + (m.content.length > 150 ? '...' : '')
    ).join('\n');
    
    // Include real entries with dates even in simple context
    const entriesWithDates = relevantEntries.slice(0, 8).map(e => {
      const realDate = new Date(e.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `• [${e.id}] ${realDate}: "${e.content.substring(0, 50)}..."`;
    }).join('\n');
    
    return '━━━ ESSENTIAL CONTEXT ━━━\n' +
      'Name: ' + context.userName + '\n' +
      'Values: ' + allValues + '\n' +
      'Goals: ' + allGoals + '\n\n' +
      '━━━ USER ENTRIES (ONLY CITE THESE) ━━━\n' + (entriesWithDates || 'No entries - do not cite any dates') + '\n' +
      '⚠️ ONLY cite entries above with their EXACT dates. Never invent dates.\n\n' +
      '━━━ RECENT MESSAGES ━━━\n' + recentMsgs;
  };
  
  const contextSection = useFullContext 
    ? buildFullContextSection()
    : conversationSummaryData 
      ? buildLightweightContext(
          context, 
          conversationSummaryData, 
          conversationHistory, 
          relevantEntries,
          enhancedContext,
          patterns,
          pendingDecisions
        )
      : buildSimpleContext();

  // ========================================
  // BUILD SYSTEM PROMPT WITH PERSONALITY + FRIEND MODE
  // ========================================
  // Get the user's selected AI personality
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);

  const systemPrompt = `${friendMode.systemRole.replace('{userName}', context.userName || '')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🤖 INTELLIGENCE LEVEL: ${aiTier.toUpperCase()} (${tierDescription.title})
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${personalitySection}
${friendMode.conversationPhases}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${friendMode.brutallyHonestRules}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${friendMode.psychologicalTechniques}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${friendMode.adaptiveTone}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${aiBehavior.citationGuidelines}

${ANTI_GENERIC_RULES}

${ANTI_HALLUCINATION_RULES}

${feedback ? buildFeedbackPromptSection(feedback, context.language) : ''}

${contextSection}

${isShortAcknowledgment ? `
🟡 MESSAGGIO BREVE ("${query}") - Rispondi con MAX 1-2 frasi. NO azioni.
` : ''}

${conversationHistory.length > 0 ? `📍 CONVERSAZIONE IN CORSO (${messageCount} messaggi)
- MANTIENI IL FILO: ricorda cosa avete discusso e costruisci su quello
- USA LE FONTI: cita entry passate che supportano la discussione attuale
- CONNETTI I PUNTI: collega quello che dice ora a quello che ha detto/scritto prima
- NON ripetere: se hai già detto qualcosa, vai avanti` : ''}

${friendMode.responseFormats}

${useFullContext ? car.entityExtraction : ''}

JSON OUTPUT:
{
  "response": "...",
  "citedEntries": [],
  "patternsUsed": [],
  "conversationSuggestions": ["exploration topic 1", "reflection prompt 2"],
  "directActions": [],
  "valuesUsed": [],
  "goalsUsed": [],
  "responseType": "support",
  "showSuggestions": true,
  "extractedEntities": {"people": [], "places": [], "decisions": []}
}

NOTE: For citedEntries, use format: [{"id": "entry_id", "date": "DD/MM/YYYY", "snippet": "quote"}]
For directActions, use: [{"type": "TRACK|DECIDE|VALUE|GOAL|PERSON", "title": "...", "reason": "..."}]

🎯 NATURAL CONVERSATION RULES:
- citedEntries: PROACTIVELY cite entries that support your response. Show the user you remember their history! If entry adds context, cite it. Citations make responses feel personal.
- directActions: Propose when user shares something meaningful. Quality over quantity. If nothing to propose, leave empty [].
- ALWAYS connect response to user's history - mention dates, people, past moments to show you KNOW them.

Action triggers (use sparingly, naturally):
- TRACK: Significant moment/emotion/realization worth remembering
- DECIDE: Clear choice or dilemma to analyze
- VALUE: Something deeply important expressed (1-2 words max)
- GOAL: Specific aspiration or objective mentioned
- PERSON: Important relationship mentioned (family, friend, partner)

The conversation should feel NATURAL like talking to a wise friend, not like filling forms.

🎯 PROACTIVE GUIDANCE (BE THE TRUSTED ADVISOR):
You are a proactive advisor, not just a listener. When the user shares something MEANINGFUL, offer:
1. A concrete action they can take NOW
2. An invitation to explore deeper if they want

PROACTIVE RESPONSE PATTERN (USE ONLY WHEN APPROPRIATE):
Include proactive guidance ONLY when the user shares something substantial. Skip for:
- Simple greetings or acknowledgments ("ok", "grazie", "si")
- Informational questions ("what time is it?", "what can you do?")
- Casual conversation without emotional weight

When proactive guidance IS appropriate, end with ONE of these (based on context):

A) When user shares a challenge:
${context.language === 'Italian' ? '💡 **Un primo passo:** [azione concreta per oggi]\n\n_Se vuoi, possiamo esplorare insieme un piano più strutturato._' :
  context.language === 'Spanish' ? '💡 **Un primer paso:** [acción concreta para hoy]\n\n_Si quieres, podemos explorar juntos un plan más estructurado._' :
  context.language === 'German' ? '💡 **Ein erster Schritt:** [konkrete Aktion für heute]\n\n_Wenn du möchtest, können wir gemeinsam einen strukturierteren Plan erkunden._' :
  context.language === 'French' ? "💡 **Un premier pas:** [action concrète pour aujourd'hui]\n\n_Si tu veux, on peut explorer ensemble un plan plus structuré._" :
  '💡 **A first step:** [concrete action for today]\n\n_If you want, we can explore a more structured plan together._'}

B) When user expresses an aspiration/goal:
${context.language === 'Italian' ? '💡 **Oggi puoi:** [micro-azione immediata]\n🎯 **Questa settimana:** [obiettivo raggiungibile]\n🚀 **Dove porta:** [visione a lungo termine]\n\n_Vuoi che approfondiamo uno di questi orizzonti?_' :
  context.language === 'Spanish' ? '💡 **Hoy puedes:** [micro-acción inmediata]\n🎯 **Esta semana:** [objetivo alcanzable]\n🚀 **Hacia dónde lleva:** [visión a largo plazo]\n\n_¿Quieres que profundicemos en alguno de estos horizontes?_' :
  context.language === 'German' ? '💡 **Heute kannst du:** [sofortige Mikro-Aktion]\n🎯 **Diese Woche:** [erreichbares Ziel]\n🚀 **Wohin es führt:** [langfristige Vision]\n\n_Möchtest du einen dieser Horizonte vertiefen?_' :
  context.language === 'French' ? "💡 **Aujourd'hui tu peux:** [micro-action immédiate]\n🎯 **Cette semaine:** [objectif atteignable]\n🚀 **Où cela mène:** [vision à long terme]\n\n_Veux-tu qu'on approfondisse un de ces horizons?_" :
  '💡 **Today you can:** [immediate micro-action]\n🎯 **This week:** [achievable goal]\n🚀 **Where it leads:** [long-term vision]\n\n_Want to explore any of these horizons deeper?_'}

C) When user shares an emotion/realization:
${context.language === 'Italian' ? '_Questa è un\'intuizione importante. Vuoi che esploriamo insieme cosa potresti fare con questa consapevolezza?_' :
  context.language === 'Spanish' ? '_Esta es una intuición importante. ¿Quieres que exploremos juntos qué podrías hacer con esta conciencia?_' :
  context.language === 'German' ? '_Das ist eine wichtige Einsicht. Möchtest du erkunden, was du mit dieser Erkenntnis anfangen könntest?_' :
  context.language === 'French' ? "_C'est une intuition importante. Veux-tu qu'on explore ensemble ce que tu pourrais faire avec cette prise de conscience?_" :
  "_This is an important insight. Want to explore what you could do with this awareness?_"}

CRITICAL RULES FOR PROACTIVE GUIDANCE:
- Actions must be SPECIFIC to THIS user's values, goals, and context - never generic
- Connect actions to what YOU know about them (patterns, past moments, people in their life)
- Make the invitation to explore MORE feel natural, not pushy
- When user says "yes" to exploring more, provide the full 3-horizon plan
- Be like a trusted friend who naturally offers help, not a coach who forces advice

${useFullContext ? `🧭 CONVERSATIONSUGGESTIОNS - TEMI DI RIFLESSIONE (NON DOMANDE!):
I suggerimenti NON sono domande che l'utente fa a te. Sono TEMI su cui TU guidi la riflessione.
Quando l'utente clicca un suggerimento, TU inizi a esplorare quel tema con loro.

❌ SBAGLIATO (domanda dell'utente): "Cosa mi consigli di fare?"
✅ GIUSTO (tema che tu esplori): "Il legame tra questa paura e i tuoi valori"

❌ SBAGLIATO: "Come posso migliorare?"
✅ GIUSTO: "Esploriamo cosa significa per te il miglioramento"

I suggerimenti devono essere:
- TEMI ESISTENZIALI: connessi ai valori, pattern, obiettivi dell'utente
- PROSPETTIVE NUOVE: angolazioni che l'utente non ha considerato
- INVITI ALLA RIFLESSIONE: "Il significato di...", "Cosa nasconde...", "Il legame tra..."
- Brevi (max 8 parole) e profondi` : `Keep conversationSuggestions relevant to current topic.`}

${car.responseFormatIntro}
Language: ${context.language}.`;

  try {
    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt }
    ];

    // TOKEN OPTIMIZATION: Use fewer history messages for ongoing conversations
    const historyLimit = useFullContext ? 6 : 4;
    const recentHistory = conversationHistory.slice(-historyLimit);
    for (const msg of recentHistory) {
      messages.push({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content
      });
    }

    messages.push({ role: 'user', content: query });

    // RETRY LOGIC: Try up to 2 times if response is empty
    let content = '';
    let retryCount = 0;
    const maxRetries = 2;
    const maxTokensForRequest = useFullContext ? 4096 : 2048;
    
    while (retryCount < maxRetries) {
      try {
        content = await aiChatCompletion(messages, { jsonMode: true, maxTokens: maxTokensForRequest });
        
        // Check if response is valid (not empty/whitespace)
        if (content && content.trim().length > 10) {
          break; // Valid response, exit retry loop
        }
        
        console.warn(`AI returned empty/short response (attempt ${retryCount + 1}/${maxRetries}), retrying...`);
        retryCount++;
        
        // If retry needed, wait a bit before retrying
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (retryError: any) {
        console.warn(`API call failed (attempt ${retryCount + 1}/${maxRetries}):`, retryError?.message);
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }
    }
    
    // Check for empty or whitespace-only responses after all retries
    if (!content || content.trim().length === 0) {
      console.warn("AI returned empty/whitespace response after all retries, using fallback");
      return {
        response: prompts.errorMessages.problemRetry || 'I could not generate a response. Please try again.',
        citedEntries: [],
        patternsUsed: [],
        suggestions: [],
        conversationSuggestions: [],
        directActions: [],
        valuesUsed: [],
        goalsUsed: [],
        emotionalMode: 'neutral' as const,
        responseType: 'support',
        showSuggestions: true,
        extractedEntities: { people: [], places: [], decisions: [] }
      };
    }
    
    // Helper to decode Unicode escapes and fix apostrophe encoding issues
    const decodeUnicodeEscapes = (text: string): string => {
      return text
        // Decode \uXXXX unicode escapes
        .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        // Decode \xXX hex escapes
        .replace(/\\x([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        // Fix malformed apostrophe encoding (27 = hex for ')
        .replace(/([a-zA-Z])27([a-zA-Z])/g, "$1'$2")
        // Clean up escaped quotes
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'");
    };

    // Try to parse JSON with fallback for malformed responses
    let parsed: any;
    try {
      parsed = JSON.parse(content);
      // Ensure response text is properly decoded
      if (parsed.response && typeof parsed.response === 'string') {
        parsed.response = decodeUnicodeEscapes(parsed.response);
      }
    } catch (jsonError) {
      console.error("JSON parsing error, attempting recovery:", jsonError);
      console.error("Raw content:", content.substring(0, 500));
      
      // Try to extract response from malformed JSON
      const responseMatch = content.match(/"response"\s*:\s*"([^"]+)"/);
      if (responseMatch) {
        parsed = { response: decodeUnicodeEscapes(responseMatch[1]) };
      } else {
        // If content looks like plain text, use it as response
        if (content && !content.startsWith('{')) {
          parsed = { response: decodeUnicodeEscapes(content.trim()) };
        } else {
          throw jsonError;
        }
      }
    }
    
    // Validate that parsed response has actual content
    const responseText = (parsed.response || '').trim();
    if (!responseText) {
      console.warn("AI returned empty response content after parsing, using fallback");
      return {
        response: prompts.errorMessages.problemRetry || 'I could not generate a response. Please try again.',
        citedEntries: [],
        patternsUsed: [],
        suggestions: [],
        conversationSuggestions: [],
        directActions: [],
        valuesUsed: [],
        goalsUsed: [],
        emotionalMode: 'neutral' as const,
        responseType: 'support',
        showSuggestions: true,
        extractedEntities: { people: [], places: [], decisions: [] }
      };
    }
    
    return {
      ...parsed,
      response: responseText,
      valuesUsed: parsed.valuesUsed || [],
      goalsUsed: parsed.goalsUsed || [],
      emotionalMode: emotionalTone,
      responseType: parsed.responseType || 'support',
      showSuggestions: parsed.showSuggestions !== false,
      conversationSuggestions: parsed.conversationSuggestions || [],
      directActions: parsed.directActions || [],
      suggestions: parsed.conversationSuggestions || parsed.suggestions || [],
      extractedEntities: parsed.extractedEntities || { people: [], places: [], decisions: [] }
    };
  } catch (error: any) {
    // Log detailed error information
    const errorType = error?.status ? 'API Error' : error instanceof SyntaxError ? 'JSON Parse Error' : 'Unknown Error';
    const errorDetails = {
      type: errorType,
      message: error?.message || 'Unknown error',
      status: error?.status,
      code: error?.code,
      query: query.substring(0, 100),
      historyLength: conversationHistory.length
    };
    console.error("Context-aware response error:", errorDetails);
    console.error("Full error:", error);
    
    // Provide more specific error messages based on error type
    let errorMessage = prompts.errorMessages.cannotProcess;
    if (error?.status === 429) {
      errorMessage = prompts.errorMessages.problemRetry || 'Too many requests, please try again in a moment.';
    } else if (error?.code === 'ECONNRESET' || error?.code === 'ETIMEDOUT') {
      errorMessage = prompts.errorMessages.problemRetry || 'Connection issue, please try again.';
    }
    
    return {
      response: errorMessage,
      citedEntries: [],
      patternsUsed: [],
      suggestions: [],
      conversationSuggestions: [],
      directActions: [],
      valuesUsed: [],
      goalsUsed: [],
      emotionalMode: 'neutral' as const,
      responseType: 'support',
      showSuggestions: true,
      extractedEntities: { people: [], places: [], decisions: [] }
    };
  }
};

// Translations for context quality labels
const CONTEXT_QUALITY_LABELS = {
  Italian: {
    name: 'Nome',
    values: 'Valori',
    goals: 'Obiettivi',
    blockers: 'Ostacoli',
    richHistory: 'Cronologia ricca',
    goodHistory: 'Buona cronologia',
    balancedLife: 'Vita bilanciata',
    deepPatterns: 'Pattern profondi',
    decisionsTracked: 'Decisioni tracciate',
    defineMoreValues: 'Definisci più valori',
    addGoals: 'Aggiungi obiettivi',
    shareChallenges: 'Condividi le tue sfide',
    keepSharingMoments: 'Continua a condividere momenti',
    exploreOtherAreas: 'Esplora altre aree della vita',
    moreDataForPatterns: 'Più dati per identificare pattern',
    trackDecisions: 'Traccia le tue decisioni'
  },
  English: {
    name: 'Name',
    values: 'Values',
    goals: 'Goals',
    blockers: 'Blockers',
    richHistory: 'Rich history',
    goodHistory: 'Good history',
    balancedLife: 'Balanced life',
    deepPatterns: 'Deep patterns',
    decisionsTracked: 'Decisions tracked',
    defineMoreValues: 'Define more values',
    addGoals: 'Add goals',
    shareChallenges: 'Share your challenges',
    keepSharingMoments: 'Keep sharing moments',
    exploreOtherAreas: 'Explore other life areas',
    moreDataForPatterns: 'More data to identify patterns',
    trackDecisions: 'Track your decisions'
  },
  Spanish: {
    name: 'Nombre',
    values: 'Valores',
    goals: 'Objetivos',
    blockers: 'Obstáculos',
    richHistory: 'Historial rico',
    goodHistory: 'Buen historial',
    balancedLife: 'Vida equilibrada',
    deepPatterns: 'Patrones profundos',
    decisionsTracked: 'Decisiones rastreadas',
    defineMoreValues: 'Define más valores',
    addGoals: 'Añade objetivos',
    shareChallenges: 'Comparte tus desafíos',
    keepSharingMoments: 'Sigue compartiendo momentos',
    exploreOtherAreas: 'Explora otras áreas de la vida',
    moreDataForPatterns: 'Más datos para identificar patrones',
    trackDecisions: 'Rastrea tus decisiones'
  },
  German: {
    name: 'Name',
    values: 'Werte',
    goals: 'Ziele',
    blockers: 'Hindernisse',
    richHistory: 'Reiche Historie',
    goodHistory: 'Gute Historie',
    balancedLife: 'Ausgeglichenes Leben',
    deepPatterns: 'Tiefe Muster',
    decisionsTracked: 'Entscheidungen verfolgt',
    defineMoreValues: 'Definiere mehr Werte',
    addGoals: 'Füge Ziele hinzu',
    shareChallenges: 'Teile deine Herausforderungen',
    keepSharingMoments: 'Teile weiterhin Momente',
    exploreOtherAreas: 'Erkunde andere Lebensbereiche',
    moreDataForPatterns: 'Mehr Daten für Muster nötig',
    trackDecisions: 'Verfolge deine Entscheidungen'
  },
  French: {
    name: 'Nom',
    values: 'Valeurs',
    goals: 'Objectifs',
    blockers: 'Obstacles',
    richHistory: 'Historique riche',
    goodHistory: 'Bon historique',
    balancedLife: 'Vie équilibrée',
    deepPatterns: 'Patterns profonds',
    decisionsTracked: 'Décisions suivies',
    defineMoreValues: 'Définis plus de valeurs',
    addGoals: 'Ajoute des objectifs',
    shareChallenges: 'Partage tes défis',
    keepSharingMoments: 'Continue à partager tes moments',
    exploreOtherAreas: 'Explore d\'autres domaines de vie',
    moreDataForPatterns: 'Plus de données pour identifier les patterns',
    trackDecisions: 'Suis tes décisions'
  }
};

// Calculate context quality score (how well AI knows the user)
export const calculateContextQuality = (
  context: LifeContext,
  entries: JournalEntry[],
  patterns: Pattern[],
  decisions: Decision[]
): {
  score: number;
  level: 'basic' | 'growing' | 'good' | 'deep' | 'excellent';
  strengths: string[];
  gaps: string[];
} => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const lang = context.language || 'English';
  const labels = CONTEXT_QUALITY_LABELS[lang] || CONTEXT_QUALITY_LABELS.English;
  let score = 0;
  const strengths: string[] = [];
  const gaps: string[] = [];

  // Profile completeness (max 20 points)
  if (context.userName) { score += 5; strengths.push(labels.name); }
  if (context.values?.length >= 2) { score += 5; strengths.push(labels.values); }
  else gaps.push(labels.defineMoreValues);
  if (context.goals?.length >= 2) { score += 5; strengths.push(labels.goals); }
  else gaps.push(labels.addGoals);
  if (context.blockers?.length >= 1) { score += 5; strengths.push(labels.blockers); }
  else gaps.push(labels.shareChallenges);

  // Entry quantity (max 30 points)
  if (safeEntries.length >= 50) { score += 30; strengths.push(labels.richHistory); }
  else if (safeEntries.length >= 20) { score += 20; strengths.push(labels.goodHistory); }
  else if (safeEntries.length >= 5) { score += 10; }
  else gaps.push(labels.keepSharingMoments);

  // Entry diversity (max 20 points)
  const categories = new Set(safeEntries.map(e => e.category));
  if (categories.size >= 4) { score += 20; strengths.push(labels.balancedLife); }
  else if (categories.size >= 2) { score += 10; }
  else gaps.push(labels.exploreOtherAreas);

  // Patterns detected (max 15 points)
  if (patterns.length >= 5) { score += 15; strengths.push(labels.deepPatterns); }
  else if (patterns.length >= 2) { score += 8; }
  else gaps.push(labels.moreDataForPatterns);

  // Decisions tracked (max 15 points)
  if (decisions.length >= 5) { score += 15; strengths.push(labels.decisionsTracked); }
  else if (decisions.length >= 1) { score += 7; }
  else gaps.push(labels.trackDecisions);

  // Determine level
  let level: 'basic' | 'growing' | 'good' | 'deep' | 'excellent' = 'basic';
  if (score >= 90) level = 'excellent';
  else if (score >= 70) level = 'deep';
  else if (score >= 50) level = 'good';
  else if (score >= 25) level = 'growing';

  return { score, level, strengths: strengths.slice(0, 4), gaps: gaps.slice(0, 3) };
};

export const generateVoiceResponse = async (
  userText: string,
  context: LifeContext,
  entries: JournalEntry[],
  patterns: Pattern[] = [],
  feedback?: UserFeedbackContext
): Promise<string> => {
  const summary = getContextSummary(context, entries, patterns);
  const p = context.aiPersonality || { warmth: 80, directness: 50, depth: 50 };

  const voicePrompts: Record<Language, string> = {
    Italian: `Sei Egovex Voice, un compagno vocale per ${context.userName}.

PERSONALITÀ:
- Calore: ${p.warmth}%
- Schiettezza: ${p.directness}%
- Profondità: ${p.depth}%

CONTESTO:
${summary}

REGOLE PER RISPOSTE VOCALI:
1. Rispondi in modo BREVE e NATURALE (1-3 frasi)
2. Usa un tono conversazionale come se parlassi a un amico
3. RIFERISCITI al contesto dell'utente
4. Evita liste o formattazione complessa
5. Sii caldo ma conciso`,
    English: `You are Egovex Voice, a voice companion for ${context.userName}.

PERSONALITY:
- Warmth: ${p.warmth}%
- Directness: ${p.directness}%
- Depth: ${p.depth}%

CONTEXT:
${summary}

VOICE RESPONSE RULES:
1. Respond BRIEFLY and NATURALLY (1-3 sentences)
2. Use a conversational tone as if talking to a friend
3. REFER to the user's context
4. Avoid lists or complex formatting
5. Be warm but concise`,
    Spanish: `Eres Egovex Voice, un compañero de voz para ${context.userName}.

PERSONALIDAD:
- Calidez: ${p.warmth}%
- Franqueza: ${p.directness}%
- Profundidad: ${p.depth}%

CONTEXTO:
${summary}

REGLAS PARA RESPUESTAS DE VOZ:
1. Responde de forma BREVE y NATURAL (1-3 frases)
2. Usa un tono conversacional como si hablaras con un amigo
3. REFIERE al contexto del usuario
4. Evita listas o formato complejo
5. Sé cálido pero conciso`,
    German: `Du bist Egovex Voice, ein Sprachbegleiter für ${context.userName}.

PERSÖNLICHKEIT:
- Wärme: ${p.warmth}%
- Direktheit: ${p.directness}%
- Tiefe: ${p.depth}%

KONTEXT:
${summary}

REGELN FÜR SPRACHANTWORTEN:
1. Antworte KURZ und NATÜRLICH (1-3 Sätze)
2. Verwende einen gesprächigen Ton wie mit einem Freund
3. BEZIEHE dich auf den Kontext des Benutzers
4. Vermeide Listen oder komplexe Formatierung
5. Sei warm aber prägnant`,
    French: `Tu es Egovex Voice, un compagnon vocal pour ${context.userName}.

PERSONNALITÉ:
- Chaleur: ${p.warmth}%
- Franchise: ${p.directness}%
- Profondeur: ${p.depth}%

CONTEXTE:
${summary}

RÈGLES POUR LES RÉPONSES VOCALES:
1. Réponds de manière BRÈVE et NATURELLE (1-3 phrases)
2. Utilise un ton conversationnel comme si tu parlais à un ami
3. RÉFÈRE-toi au contexte de l'utilisateur
4. Évite les listes ou le formatage complexe
5. Sois chaleureux mais concis`
  };

  const feedbackSection = feedback ? buildFeedbackPromptSection(feedback, context.language) : '';
  const systemPrompt = (voicePrompts[context.language] || voicePrompts.English) + '\n\n' + feedbackSection;

  try {
    const result = await aiChatCompletion(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userText }
      ],
      { maxTokens: 256 }
    );
    const prompts = getPrompts(context.language);
    return result || prompts.errorMessages.didNotUnderstand;
  } catch {
    const prompts = getPrompts(context.language);
    return prompts.errorMessages.problemRetry;
  }
};

export const generateAgentGreeting = async (
  openingContext: ChatOpeningContext,
  context: LifeContext,
  entries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[] = [],
  feedback?: UserFeedbackContext
): Promise<{
  greeting: string;
  citedEntries: Array<{ id: string; date: string; snippet: string }>;
  patternsUsed: string[];
  suggestions: string[];
}> => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const summary = getContextSummary(context, entries, patterns, decisions);
  const p = context.aiPersonality || { warmth: 80, directness: 50, depth: 50 };
  const prompts = getPrompts(context.language);
  const ag = prompts.agentGreeting;

  let contextSpecificPrompt = '';
  let relevantData = '';

  switch (openingContext.type) {
    case 'pulse_insight':
      contextSpecificPrompt = `${ag.pulseInsightPrompt}: "${openingContext.data?.insightText || 'insight'}"`;
      break;
    
    case 'decision_analysis':
      const decision = decisions.find(d => d.id === openingContext.data?.decisionId);
      relevantData = decision ? `
${ag.decisionToDiscuss}
• ${ag.title}: ${decision.title}
• ${ag.context}: ${decision.context}
• ${ag.status}: ${decision.status}
• ${ag.options}: ${decision.options?.map(o => o.title).join(', ') || ag.toExplore}` : '';
      contextSpecificPrompt = `${ag.decisionAnalysisPrompt}: "${openingContext.data?.decisionTitle}".
${relevantData}`;
      break;

    case 'knowledge_gaps':
      contextSpecificPrompt = `${ag.knowledgeGapsPrompt}: "${openingContext.data?.gapArea}".`;
      break;

    case 'goal_progress':
      const goalEntries = safeEntries.filter(e => 
        e.content.toLowerCase().includes((openingContext.data?.goalName || '').toLowerCase())
      ).slice(0, 3);
      relevantData = goalEntries.length > 0 ? `
${ag.momentsRelatedToGoal}
${goalEntries.map(e => `• ${new Date(e.timestamp).toLocaleDateString(context.language === 'Italian' ? 'it-IT' : context.language === 'French' ? 'fr-FR' : context.language === 'Spanish' ? 'es-ES' : context.language === 'German' ? 'de-DE' : 'en-US')}: "${e.content.substring(0, 80)}..."`).join('\n')}` : '';
      contextSpecificPrompt = `${ag.goalProgressPrompt}: "${openingContext.data?.goalName}".
${relevantData}`;
      break;

    case 'pattern_discussion':
      const pattern = patterns.find(p => p.title === openingContext.data?.patternTitle);
      relevantData = pattern ? `
${ag.patternInQuestion}
• ${pattern.isPositive ? `✅ ${ag.positive}` : `⚠️ ${ag.toObserve}`}: ${pattern.title}
• ${ag.description}: ${pattern.description}
• ${ag.insight}: ${pattern.insight}
• ${pattern.frequency} ${ag.seenTimes}` : '';
      contextSpecificPrompt = `${ag.patternDiscussionPrompt}: "${openingContext.data?.patternTitle}".
${relevantData}`;
      break;

    case 'decision_next_step':
      const nextStepData = openingContext.data;
      const optionsSummary = nextStepData?.options?.map(o => {
        const pros = (o.pros || []).slice(0, 2).join(', ') || ag.none;
        const cons = (o.cons || []).slice(0, 2).join(', ') || ag.none;
        return `• ${o.title} (${o.alignmentScore || 0}% ${ag.aligned}) - ${ag.pros}: ${pros} | ${ag.cons}: ${cons}`;
      }).join('\n') || ag.none;
      relevantData = `
${ag.decisionAnalyzed}
• ${ag.title}: ${nextStepData?.decisionTitle}
• ${ag.suggestedOption}: ${nextStepData?.bestOption}
• ${ag.whyReason}: ${nextStepData?.reasonWhy}
• ${ag.plannedNextStep}: "${nextStepData?.nextAction}"

${ag.allOptionsEvaluated}
${optionsSummary}`;
      contextSpecificPrompt = `${ag.decisionNextStepPrompt}: "${nextStepData?.decisionTitle}" → "${nextStepData?.nextAction}".

${relevantData}

🎯 ${ag.focusHelp}

${ag.actionableResponse}

${ag.suggestionsMustBe}`;
      break;

    case 'decision_help':
      const pendingDecs = decisions.filter(d => d.status === 'pending').slice(0, 3);
      const recentDecisionMoments = safeEntries
        .filter(e => {
          const lower = e.content.toLowerCase();
          return lower.includes('decid') || lower.includes('scelt') || lower.includes('non so se') || 
                 lower.includes('dovrei') || lower.includes('vorrei') || lower.includes('bivio') ||
                 lower.includes('decide') || lower.includes('choice') || lower.includes('should i') ||
                 lower.includes('décid') || lower.includes('choix') || lower.includes('je ne sais pas si') ||
                 lower.includes('entscheid') || lower.includes('wahl') || lower.includes('ich weiß nicht');
        })
        .slice(0, 3);
      
      const langLocale = context.language === 'Italian' ? 'it-IT' : context.language === 'French' ? 'fr-FR' : context.language === 'Spanish' ? 'es-ES' : context.language === 'German' ? 'de-DE' : 'en-US';
      
      relevantData = pendingDecs.length > 0 || recentDecisionMoments.length > 0 ? `
${ag.decisionsInProgress}
${pendingDecs.map(d => `• "${d.title}" - ${ag.stillToResolve}`).join('\n') || ag.noOpenDecisions}

${ag.recentMomentsLookLikeDecisions}
${recentDecisionMoments.map(e => `• ${new Date(e.timestamp).toLocaleDateString(langLocale)}: "${e.content.substring(0, 60)}..."`).join('\n') || ag.noRelevantMoments}` : '';
      
      contextSpecificPrompt = `${ag.decisionHelpPrompt}

${relevantData}

🎯 ${ag.yourApproach}

${ag.generateSuggestions}
- ${ag.alwaysInclude}: "${ag.tellSomethingNew}"

${ag.noGenericPhrases}`;
      break;

    default:
      contextSpecificPrompt = ag.defaultPrompt;
  }

  // Use friend mode and personality for greeting
  const friendMode = getFriendModePrompts(context.language);
  const feedbackSection = feedback ? buildFeedbackPromptSection(feedback, context.language) : '';
  const personalitySection = buildPersonalityPromptSection(context.selectedPersonalityKey, context.language);

  const systemPrompt = `${friendMode.systemRole.replace('{userName}', context.userName || 'user')}
${personalitySection}
📚 CHI È ${context.userName?.toUpperCase() || 'L\'UTENTE'}:
${summary}

${feedbackSection}

━━━━━━━━━━━━━━━━━━━━━━━
SITUAZIONE SPECIFICA:
━━━━━━━━━━━━━━━━━━━━━━━
${contextSpecificPrompt}

━━━━━━━━━━━━━━━━━━━━━━━
${friendMode.conversationPhases}
━━━━━━━━━━━━━━━━━━━━━━━

${friendMode.adaptiveTone}

⚡ REGOLE PER IL PRIMO MESSAGGIO:
- Sii DIRETTO e SPECIFICO - usa i dati che hai
- NON fare introduzioni generiche ("Ciao, come stai?")
- Vai subito al punto basandoti sulla SITUAZIONE SPECIFICA sopra
- Mostra che CONOSCI già l'utente citando dati reali
- Termina con UNA domanda breve e specifica (max 8 parole)
- Tono da amico che sa già tutto, non da sconosciuto

ESEMPI DI APERTURA (adatta al contesto):
❌ "Ciao! Come posso aiutarti oggi con le tue decisioni?"
✅ "Ehi, vedo che stai ancora rimuginando su [decisione]. L'ultima volta il [data]..."

❌ "Capisco che vuoi parlare di [tema]. Cosa ti preoccupa?"
✅ "Ok, [tema] di nuovo. È la terza volta in 2 settimane. Cosa sta succedendo davvero?"

FORMATO JSON:
{
  "greeting": "messaggio diretto e specifico basato sul contesto",
  "citedEntries": [{"id": "...", "date": "DD/MM/YYYY", "snippet": "..."}],
  "patternsUsed": ["pattern rilevanti"],
  "suggestions": ["tema da esplorare 1", "tema da esplorare 2", "tema da esplorare 3"]
}

I suggestions devono essere TEMI che TU esplori con l'utente, NON domande dell'utente.

Language: ${context.language}.`;

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `${ag.userMessage} (${openingContext.type})` }
      ],
      { jsonMode: true, maxTokens: 4096 }
    );
    
    // Try to parse JSON with fallback for malformed responses
    let parsed: any;
    try {
      parsed = JSON.parse(content);
    } catch (jsonError) {
      console.error("Agent greeting JSON parsing error:", jsonError);
      console.error("Raw content:", content.substring(0, 500));
      
      // Try to extract greeting from malformed JSON
      const greetingMatch = content.match(/"greeting"\s*:\s*"([^"]+)"/);
      if (greetingMatch) {
        parsed = { greeting: greetingMatch[1], citedEntries: [], patternsUsed: [], suggestions: [] };
      } else {
        throw jsonError;
      }
    }
    
    return {
      greeting: parsed.greeting || '',
      citedEntries: parsed.citedEntries || [],
      patternsUsed: parsed.patternsUsed || [],
      suggestions: parsed.suggestions || []
    };
  } catch (error: any) {
    const errorDetails = {
      type: error?.status ? 'API Error' : error instanceof SyntaxError ? 'JSON Parse Error' : 'Unknown Error',
      message: error?.message || 'Unknown error',
      status: error?.status,
      code: error?.code,
      openingType: openingContext.type
    };
    console.error("Agent greeting generation error:", errorDetails);
    console.error("Full error:", error);
    
    const prompts = getPrompts(context.language);
    const err = prompts.errorMessages;
    return {
      greeting: context.userName 
        ? `${context.userName}, ${err.hereToHelp}`
        : err.hereToHelp,
      citedEntries: [],
      patternsUsed: [],
      suggestions: [err.howDoYouFeel, err.somethingWeighing]
    };
  }
};

// Whisper transcription for voice notes (via backend)
export const transcribeWithWhisper = async (audioBlob: Blob, language: string = 'it'): Promise<string> => {
  const buffer = await audioBlob.arrayBuffer();
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
  
  const response = await fetch('/api/ai/transcribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ audio: base64, language }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Transcription failed');
  }

  const data = await response.json();
  return data.text?.trim() || '';
};
